import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Breadcrumbs,
	Checkbox,
	Chip,
	Collapse,
	Dialog,
	Drawer,
	FormControlLabel,
	IconButton,
	Link,
	MenuItem,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CustomButton from '../components/v2/general/CustomButton/CustomButton'
import SchedulesStatusSwitch from '../components/Schedules/Header/SchedulesStatusSwitch'
import driversMock from '../data/mockedData/schedules/drivers.json'
import statusDashboardMock from '../data/mockedData/schedules/statusDashboard.json'
import { useModal } from '../store/ModalProvider'
import { dataGridDensityTransitionSx } from '../components/v2/general/CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../components/v2/general/CustomDataGrid/CustomColumnMenu'
import { useParams } from 'react-router-dom'
import type { DriverServiceTotalsByTypeRow, DriverServiceTotalsModalData, DriverServiceTotalsRow } from '../types/modal'

interface Driver {
	id: number
	number?: string
	name: string
	location?: string
	phone?: string
}

interface StatusDashboardAlert {
	id: string
	title: string
	timestamp: string
}

interface StatusDashboardData {
	services: {
		total: number
		escalados: number
		subcontracted: number
		porEscalar: number
		missingDriver: number
		missingVehicle: number
	}
	coverage: {
		total: number
		covered: number
		incomplete: number
	}
	drivers: {
		total: number
		escalados: number
		naoEscalados: number
		fora: number
		alerts: Array<{ id: string; label: string; count: number }>
	}
	vehicles: {
		total: number
		escalados: number
		naoEscalados: number
		emOficina: number
		alerts: Array<{ id: string; label: string; count: number }>
	}
	alerts: {
		unread: StatusDashboardAlert[]
		read: StatusDashboardAlert[]
	}
}

interface DriverSituationRow {
	id: string
	inicio: string
	fim: string
	dias: string
	idSituacao: string
	situacao: string
	descricao: string
	idRegisto: string
	idRegistoOk: string
	utilizador: string
	dataRegisto: string
}

type DonutGradientKey = 'green' | 'orange' | 'blue' | 'gray' | 'amber'

interface DonutSegment {
	value: number
	colorStart: string
	colorEnd: string
	gradientKey?: DonutGradientKey
}

const donutGradients = {
	blue: { start: '#00A5FF', end: '#37CDFF' },
	green: { start: '#16C53D', end: '#00A5FF' },
	orange: { start: '#FF5C00', end: '#EDBF67' },
	gray: { start: '#8A97B2', end: '#C7D1E0' },
	amber: { start: '#F5A623', end: '#F8C56B' },
}

const breadcrumbLinkSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '11px',
	lineHeight: 1.5,
	color: 'var(--primary-light)',
	textDecoration: 'none',
}

const breadcrumbCurrentSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '11px',
	lineHeight: 1.5,
	color: '#00A5FF',
}

const headerFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: 'var(--primary-dark)',
		lineHeight: '12px',
		letterSpacing: '0.15px',
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: 'var(--primary-dark)',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const headerDisabledFieldSx = {
	...headerFieldSx,
	'& .MuiInputLabel-root': {
		...headerFieldSx['& .MuiInputLabel-root'],
		color: 'var(--primary-dark)',
	},
	'& .MuiOutlinedInput-root': {
		...headerFieldSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#F5F7FC',
		'& fieldset': {
			borderColor: 'transparent',
		},
	},
	'& .MuiOutlinedInput-input': {
		...headerFieldSx['& .MuiOutlinedInput-input'],
		color: 'var(--primary-dark)',
	},
}

const cardFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: 'var(--primary-dark)',
		lineHeight: '12px',
		letterSpacing: '0.15px',
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '33px',
		fontSize: '13px',
		fontWeight: 500,
		color: 'var(--primary-dark)',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const disabledCardFieldSx = {
	...cardFieldSx,
	'& .MuiInputLabel-root': {
		...cardFieldSx['& .MuiInputLabel-root'],
		color: 'var(--primary-light)',
	},
	'& .MuiOutlinedInput-root': {
		...cardFieldSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#F5F7FC',
		'& fieldset': {
			borderColor: 'transparent',
		},
	},
	'& .MuiOutlinedInput-input': {
		...cardFieldSx['& .MuiOutlinedInput-input'],
		color: 'var(--primary-light)',
	},
}

const expiredCardFieldSx = {
	...cardFieldSx,
	'& .MuiOutlinedInput-root': {
		...cardFieldSx['& .MuiOutlinedInput-root'],
		'& fieldset': {
			borderColor: '#FF5C00',
		},
		'&:hover fieldset': {
			borderColor: '#FF5C00',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#FF5C00',
		},
	},
}

const sectionTitleSx = {
	fontFamily: 'var(--font-family)',
	fontWeight: 600,
	fontSize: '16px',
	lineHeight: 1.75,
	letterSpacing: '0.15px',
	color: 'var(--primary-dark)',
}

const checkboxLabelSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '13px',
	fontWeight: 500,
	color: 'var(--primary-dark)',
}

const checkboxSx = {
	color: 'rgba(134, 149, 175, 0.6)',
	'&.Mui-checked': { color: '#00A5FF' },
}

const alertBoxSx = {
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	padding: '7px 16px',
	borderRadius: '4px',
	backgroundImage: 'linear-gradient(106.589deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
}

const alertTextSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '14px',
	fontWeight: 500,
	lineHeight: 1.5,
	letterSpacing: '0.15px',
	color: '#FFFFFF',
	flex: 1,
}

const alertActionButtonSx = {
	color: '#FFFFFF',
	fontWeight: 600,
	fontSize: '14px',
	lineHeight: '22px',
	textTransform: 'none',
	padding: '4px 6px',
	minWidth: 'unset',
}

const serviceEscalasRows = [
	{
		id: '1',
		center: 'Escalas Transol',
		type: 'Agências Transol',
		typeId: '3',
		centerId: '1',
		canChange: 'Sim',
		filial: '001',
	},
	{
		id: '2',
		center: 'Escalas Transol',
		type: 'Ocasionais Transol',
		typeId: '2',
		centerId: '1',
		canChange: 'Sim',
		filial: '001',
	},
	{
		id: '3',
		center: 'Escalas Transol',
		type: 'Fixos Transol',
		typeId: '1',
		centerId: '1',
		canChange: 'Não',
		filial: '001',
	},
	{
		id: '4',
		center: 'Follow Me',
		type: 'Fixos Follow Me',
		typeId: '330',
		centerId: '33',
		canChange: 'Sim',
		filial: '033',
	},
]

const serviceBlockedClientsRows = [
	{
		id: '1',
		name: 'Nome do Cliente',
		idCliente: '1234',
		localidade: 'Lisboa',
		contribuinte: '000000000',
		dataReal: '',
		utWindows: 'sim',
		utApp: 'Sim',
	},
]

const folgasAtrasadasRows = [
	{
		id: '1',
		dataMov: '10/02/2024',
		tipo: 'CR',
		descricao: 'Folga Trabalhada',
		folgas: '1',
		folgasDb: '1',
		saldo: '1',
		idConfig: '5',
		codMov: '1',
	},
	{
		id: '2',
		dataMov: '15/01/2024',
		tipo: 'CR',
		descricao: 'Folga Trabalhada',
		folgas: '1',
		folgasDb: '1',
		saldo: '1',
		idConfig: '5',
		codMov: '1',
	},
	{
		id: '3',
		dataMov: '08/01/2024',
		tipo: 'CR',
		descricao: 'Folga Trabalhada',
		folgas: '1',
		folgasDb: '1',
		saldo: '1',
		idConfig: '5',
		codMov: '1',
	},
]

const descansosAposFolgaRows = [
	{
		id: '1',
		dataMov: '05/02/2024',
		tipo: 'CR',
		descricao: 'Folga Trabalhada',
		folgaTrabalhada: '1',
		complementarTrabalhada: '0',
		descanso: '1',
		saldo: '1',
		idConfig: '5',
		codMov: '1',
	},
	{
		id: '2',
		dataMov: '18/02/2024',
		tipo: 'CR',
		descricao: 'Complementar Trabalhada',
		folgaTrabalhada: '0',
		complementarTrabalhada: '1',
		descanso: '1',
		saldo: '1',
		idConfig: '5',
		codMov: '1',
	},
	{
		id: '3',
		dataMov: '26/02/2024',
		tipo: 'CR',
		descricao: 'Folga Trabalhada',
		folgaTrabalhada: '1',
		complementarTrabalhada: '0',
		descanso: '1',
		saldo: '2',
		idConfig: '5',
		codMov: '1',
	},
]

type CalendarTag = { label: string; color: 'blue' | 'green' | 'orange' }
type CalendarCell = string | CalendarTag | null

const calendarColumnsSelected = ['Semana', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Alterado', 'Alterado']

const calendarColumnsDefault = ['Semana', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const selectedMonthRows: CalendarCell[][] = [
	[
		'1',
		null,
		null,
		null,
		null,
		{ label: 'Folga 01 Jan', color: 'blue' },
		{ label: 'Compl 02 Jan', color: 'green' },
		'03 Jan',
		null,
		null,
	],
	[
		'2',
		'04 Jan',
		'05 Jan',
		'06 Jan',
		'07 Jan',
		{ label: 'Folga 08 Jan', color: 'blue' },
		{ label: 'Compl 09 Jan', color: 'green' },
		'10 Jan',
		null,
		null,
	],
	[
		'3',
		'11 Jan',
		'12 Jan',
		{ label: 'Baixa 13 Jan', color: 'orange' },
		'14 Jan',
		{ label: 'Folga 15 Jan', color: 'blue' },
		{ label: 'Compl 16 Jan', color: 'green' },
		'17 Jan',
		null,
		null,
	],
	[
		'4',
		'18 Jan',
		'19 Jan',
		'20 Jan',
		'21 Jan',
		{ label: 'Folga 22 Jan', color: 'blue' },
		{ label: 'Compl 23 Jan', color: 'green' },
		'24 Jan',
		null,
		null,
	],
	[
		'5',
		'25 Jan',
		'26 Jan',
		'27 Jan',
		'28 Jan',
		{ label: 'Folga 29 Jan', color: 'blue' },
		{ label: 'Compl 30 Jan', color: 'green' },
		'31 Jan',
		null,
		null,
	],
]

const previousMonthRows: CalendarCell[][] = [
	[
		'49',
		null,
		'01 Dez',
		'02 Dez',
		'03 Dez',
		{ label: 'Folga 04 Dez', color: 'blue' },
		{ label: 'Compl 05 Dez', color: 'green' },
		'06',
	],
	[
		'50',
		'07 Dez',
		'08 Dez',
		'09 Dez',
		'10 Dez',
		{ label: 'Folga 11 Dez', color: 'blue' },
		{ label: 'Compl 12 Dez', color: 'green' },
		'13',
	],
	[
		'51',
		'14 Dez',
		'15 Dez',
		'16 Dez',
		'17 Dez',
		{ label: 'Folga 18 Dez', color: 'blue' },
		{ label: 'Compl 19 Dez', color: 'green' },
		'20',
	],
	[
		'52',
		'21 Dez',
		'22 Dez',
		'23 Dez',
		'24 Dez',
		{ label: 'Folga 25 Dez', color: 'blue' },
		{ label: 'Compl 26 Dez', color: 'green' },
		'27',
	],
	['52', '28 Dez', '29 Dez', '30 Dez', '31 Dez', null, null, null],
]

const nextMonthRows: CalendarCell[][] = [
	[
		'6',
		'01 Fev',
		'02 Fev',
		'03 Fev',
		'04 Fev',
		{ label: 'Folga 05 Fev', color: 'blue' },
		{ label: 'Compl 06 Fev', color: 'green' },
		'07',
	],
	[
		'7',
		'08 Fev',
		'09 Fev',
		'10 Fev',
		'11 Fev',
		{ label: 'Folga 12 Fev', color: 'blue' },
		{ label: 'Compl 13 Fev', color: 'green' },
		'14',
	],
	[
		'8',
		'15 Fev',
		'16 Fev',
		'17 Fev',
		'18 Fev',
		{ label: 'Folga 19 Fev', color: 'blue' },
		{ label: 'Compl 20 Fev', color: 'green' },
		'21',
	],
	[
		'9',
		'22 Fev',
		'23 Fev',
		'24 Fev',
		'25 Fev',
		{ label: 'Folga 26 Fev', color: 'blue' },
		{ label: 'Compl 27 Fev', color: 'green' },
		'28',
	],
]

const calendarGroupMembers = [
	{ id: '1', name: 'Mario Santos', nr: '01283', nrMec: '01283', driverId: '10302' },
	{ id: '2', name: 'João Pereira', nr: '01284', nrMec: '01284', driverId: '10303' },
	{ id: '3', name: 'Ana Costa', nr: '01285', nrMec: '01285', driverId: '10304' },
	{ id: '4', name: 'Lucas Almeida', nr: '01286', nrMec: '01286', driverId: '10305' },
	{ id: '5', name: 'Beatriz Costa', nr: '01298', nrMec: '01298', driverId: '10317' },
	{ id: '6', name: 'Carlos Silva', nr: '01288', nrMec: '01288', driverId: '10307' },
	{ id: '7', name: 'Juliana Martins', nr: '01289', nrMec: '01289', driverId: '10308' },
	{ id: '8', name: 'Pedro Gomes', nr: '01290', nrMec: '01290', driverId: '10309' },
	{ id: '9', name: 'Tatiane Rocha', nr: '01291', nrMec: '01291', driverId: '10310' },
	{ id: '10', name: 'Bruno Ferreira', nr: '01292', nrMec: '01292', driverId: '10311' },
	{ id: '11', name: 'Camila Souza', nr: '01293', nrMec: '01293', driverId: '10312' },
	{ id: '12', name: 'Diego Cardoso', nr: '01294', nrMec: '01294', driverId: '10313' },
	{ id: '13', name: 'Priscila Mendes', nr: '01295', nrMec: '01295', driverId: '10314' },
	{ id: '14', name: 'Felipe Dias', nr: '01296', nrMec: '01296', driverId: '10315' },
	{ id: '15', name: 'Tatiane Vasquez', nr: '01297', nrMec: '01297', driverId: '10316' },
]

const consultItems = [
	'AS400',
	'Serviços e Totais do Motorista',
	'Situações do Motorista',
	'Sinistralidade',
	'Declarações de Atividade',
	'Tacógrafo',
	'Calendário de Folgas',
	'Descansos após Folga / Complementar Trabalhada',
]

const servicesTotalsRows = [
	{
		id: '1',
		ponto: 'Sim',
		requisicao: '95509',
		viatura: '0153',
		dataInicio: '02/05/2024',
		horaInicio: '09:00',
		localInicio: 'Lisboa',
		localTermino: 'Torres Vedras',
		horaTermino: '10:00',
		dataTermino: '02/05/2024',
		cliente: 'Best Transfers 4 U',
		valor: '90,85€',
	},
	{
		id: '2',
		ponto: 'Sim',
		requisicao: '84211',
		viatura: '0194',
		dataInicio: '03/05/2024',
		horaInicio: '14:00',
		localInicio: 'Lisboa',
		localTermino: 'Almada',
		horaTermino: '15:00',
		dataTermino: '03/05/2024',
		cliente: 'Scenic Tours',
		valor: '90,00€',
	},
	{
		id: '3',
		ponto: 'Não',
		requisicao: '84212',
		viatura: '0195',
		dataInicio: '03/05/2024',
		horaInicio: '09:00',
		localInicio: 'Braga',
		localTermino: 'Guimarães',
		horaTermino: '10:30',
		dataTermino: '03/05/2024',
		cliente: 'Cultural Walks',
		valor: '60,00€',
	},
	{
		id: '4',
		ponto: 'Sim',
		requisicao: '84213',
		viatura: '0196',
		dataInicio: '04/05/2024',
		horaInicio: '16:00',
		localInicio: 'Coimbra',
		localTermino: 'Figueira da Foz',
		horaTermino: '17:30',
		dataTermino: '04/05/2024',
		cliente: 'Sunset Escapes',
		valor: '80,00€',
	},
	{
		id: '5',
		ponto: 'Sim',
		requisicao: '84214',
		viatura: '0197',
		dataInicio: '05/05/2024',
		horaInicio: '09:00',
		localInicio: 'Lisboa',
		localTermino: 'Porto',
		horaTermino: '12:30',
		dataTermino: '05/05/2024',
		cliente: 'Urban Adventures',
		valor: '75,00€',
	},
	{
		id: '6',
		ponto: 'Sim',
		requisicao: '84215',
		viatura: '0198',
		dataInicio: '06/05/2024',
		horaInicio: '11:00',
		localInicio: 'Funchal',
		localTermino: 'Ponta Delgada',
		horaTermino: '14:15',
		dataTermino: '06/05/2024',
		cliente: 'Island Hopping',
		valor: '90,00€',
	},
	{
		id: '7',
		ponto: 'Sim',
		requisicao: '84216',
		viatura: '0199',
		dataInicio: '07/05/2024',
		horaInicio: '13:00',
		localInicio: 'Torres Vedras',
		localTermino: 'Lisboa',
		horaTermino: '14:00',
		dataTermino: '07/05/2024',
		cliente: 'Cultural Heritage',
		valor: '70,00€',
	},
	{
		id: '8',
		ponto: 'Sim',
		requisicao: '84217',
		viatura: '0200',
		dataInicio: '08/05/2024',
		horaInicio: '18:00',
		localInicio: 'Lisboa',
		localTermino: 'Setúbal',
		horaTermino: '20:30',
		dataTermino: '08/05/2024',
		cliente: 'Scenic Tours',
		valor: '85,00€',
	},
]

const servicesTotalsByTypeRows = [
	{
		id: '1',
		tipo: 'Agências Viagens',
		numero: '5',
		valor: '405,90',
		kmsCarga: '750',
		kmsVazio: '50',
		totalKms: '800',
	},
	{
		id: '2',
		tipo: 'Ocasional',
		numero: '10',
		valor: '525,00',
		kmsCarga: '1140',
		kmsVazio: '196',
		totalKms: '1336',
	},
	{
		id: '3',
		tipo: 'Totais',
		numero: '15',
		valor: '930,90',
		kmsCarga: '1890',
		kmsVazio: '246',
		totalKms: '2136',
	},
]

const tachoRows = [
	{
		id: '1',
		numMec: '12345',
		data: '15/03/2024',
		dataReal: '15/03/2024',
		nomeFicheiro: 'Lorem Ipsum',
		nome: 'Lorem Ipsum',
	},
	{
		id: '2',
		numMec: '12345',
		data: '16/03/2024',
		dataReal: '16/03/2024',
		nomeFicheiro: 'Lorem Ipsum',
		nome: 'Lorem Ipsum',
	},
]

const driverSituationsGridRows: DriverSituationRow[] = [
	{
		id: '1',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		dias: '0',
		idSituacao: '3',
		situacao: 'Folga Atrasada',
		descricao: 'Lorem Ipsum',
		idRegisto: '73252',
		idRegistoOk: '000073252',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '2',
		inicio: 'sáb 01/06/2024',
		fim: 'sex 01/07/2024',
		dias: '28',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '—',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '3',
		inicio: 'sáb 02/07/2024',
		fim: 'sex 03/08/2024',
		dias: '31',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '—',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
]

const sinistralidadeRows = [
	{
		id: '1',
		numero: '12345',
		data: '15/03/2024',
		matricula: 'XXXXXX',
		viatura: '0189',
		custoObra: '120,00',
		valorPago: '120,00',
		valorRecebido: '152,00',
		diasImobilizacao: '2',
		localSinistro: 'Lisboa',
		causa: 'Lorem Ipsum',
	},
	{
		id: '2',
		numero: '12345',
		data: '22/09/2024',
		matricula: 'XXXXXX',
		viatura: '0189',
		custoObra: '900,00',
		valorPago: '700,00',
		valorRecebido: '00,00',
		diasImobilizacao: '8',
		localSinistro: 'Lisboa',
		causa: 'Lorem Ipsum',
	},
]

const tableHeaderTextSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: '24px',
	letterSpacing: '0.17px',
	color: 'var(--primary-light)',
}

const tableCellTextSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '12px',
	fontWeight: 400,
	lineHeight: 1.3,
	color: 'var(--primary-dark)',
}

const tableDensityToggleSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	backgroundColor: '#FFFFFF',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': { backgroundColor: '#F5F7FC' },
		},
	},
	'& .MuiToggleButton-root:first-of-type': {
		borderTopLeftRadius: '4px',
		borderBottomLeftRadius: '4px',
	},
	'& .MuiToggleButton-root:last-of-type': {
		borderTopRightRadius: '4px',
		borderBottomRightRadius: '4px',
	},
}

const dataGridBaseSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...tableHeaderTextSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...tableCellTextSx,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const servicesTotalsColumns: GridColDef<DriverServiceTotalsRow>[] = [
	{ field: 'ponto', headerName: 'Ponto', width: 68 },
	{
		field: 'requisicao',
		headerName: 'Requisição',
		width: 101,
	},
	{ field: 'viatura', headerName: 'Viatura', width: 79 },
	{
		field: 'dataInicio',
		headerName: 'Data Inicio',
		width: 100,
	},
	{
		field: 'horaInicio',
		headerName: 'Hora Inicio',
		width: 99,
	},
	{
		field: 'localInicio',
		headerName: 'Local Inicio',
		width: 120,
	},
	{
		field: 'localTermino',
		headerName: 'Local Término',
		width: 120,
	},
	{
		field: 'horaTermino',
		headerName: 'Hora Término',
		width: 117,
	},
	{
		field: 'dataTermino',
		headerName: 'Data Término',
		width: 117,
	},
	{
		field: 'cliente',
		headerName: 'Cliente',
		width: 138,
	},
	{ field: 'valor', headerName: 'Valor', width: 90 },
]

const servicesTotalsByTypeColumns: GridColDef<DriverServiceTotalsByTypeRow>[] = [
	{ field: 'tipo', headerName: 'Tipo', width: 140 },
	{
		field: 'numero',
		headerName: 'Número Serviços',
		width: 138,
	},
	{ field: 'valor', headerName: 'Valor (€)', width: 100 },
	{ field: 'kmsCarga', headerName: 'Kms Carga', width: 100 },
	{ field: 'kmsVazio', headerName: 'Kms Vazio', width: 100 },
	{ field: 'totalKms', headerName: 'Total Kms', width: 100 },
]

const driverSituationsColumns: GridColDef<DriverSituationRow>[] = [
	{
		field: 'inicio',
		headerName: 'Início',
		minWidth: 140,
		flex: 1,
	},
	{
		field: 'fim',
		headerName: 'Fim',
		minWidth: 140,
		flex: 1,
	},
	{
		field: 'dias',
		headerName: 'Dias',
		width: 70,
	},
	{
		field: 'idSituacao',
		headerName: 'ID Situação',
		minWidth: 110,
		flex: 0.8,
	},
	{
		field: 'situacao',
		headerName: 'Situação',
		minWidth: 130,
		flex: 1,
	},
	{
		field: 'descricao',
		headerName: 'Descrição',
		minWidth: 155,
		flex: 1.2,
	},
	{
		field: 'idRegisto',
		headerName: 'ID Registo',
		minWidth: 110,
		flex: 0.8,
	},
	{
		field: 'idRegistoOk',
		headerName: 'ID Registo OK',
		minWidth: 125,
		flex: 0.9,
	},
	{
		field: 'utilizador',
		headerName: 'Utilizador',
		minWidth: 125,
		flex: 1,
	},
	{
		field: 'dataRegisto',
		headerName: 'Data Registo',
		minWidth: 130,
		flex: 1,
	},
]

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
	const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
	return {
		x: centerX + radius * Math.cos(angleInRadians),
		y: centerY + radius * Math.sin(angleInRadians),
	}
}

const describeArc = (centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number) => {
	const start = polarToCartesian(centerX, centerY, radius, endAngle)
	const end = polarToCartesian(centerX, centerY, radius, startAngle)
	const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

	return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`
}

const getDonutGradientCoords = (size: number, key?: DonutGradientKey) => {
	if (key === 'orange') {
		return {
			x1: size * 0.18896,
			y1: size * 0,
			x2: size * 0.17527,
			y2: size * 1.84602,
		}
	}

	return {
		x1: size * 1.75293,
		y1: size * -0.77977,
		x2: size * 2.04152,
		y2: size * 1.17267,
	}
}

const DonutChart = ({
	value,
	total,
	segments,
	size = 96,
	thickness = 12,
}: {
	value: number
	total: number
	segments: DonutSegment[]
	size?: number
	thickness?: number
}) => {
	const resolvedThickness = thickness ?? Math.round(size * 0.2)
	const innerSize = size - resolvedThickness * 2
	const radius = (size - resolvedThickness) / 2
	const center = size / 2
	const gapAngle = 6
	const idPrefix = React.useId()
	const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0) || 1

	let cumulativeAngle = 0
	const arcSegments = segments
		.filter(segment => segment.value > 0)
		.map((segment, index, activeSegments) => {
			const rawAngle = (segment.value / totalValue) * 360
			const effectiveGap = activeSegments.length === 1 ? 0 : Math.min(gapAngle, rawAngle * 0.4)
			const startAngle = cumulativeAngle + effectiveGap / 2
			const endAngle = cumulativeAngle + rawAngle - effectiveGap / 2
			cumulativeAngle += rawAngle
			if (endAngle <= startAngle) {
				return null
			}

			const gradientId = `${idPrefix}-seg-${index}`
			return {
				path: describeArc(center, center, radius, startAngle, endAngle),
				gradientId,
				gradientCoords: getDonutGradientCoords(size, segment.gradientKey),
				colors: {
					start: segment.colorStart,
					end: segment.colorEnd,
				},
			}
		})
		.filter((segment): segment is NonNullable<typeof segment> => segment !== null)

	return (
		<Box
			sx={{
				width: size,
				height: size,
				borderRadius: '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				position: 'relative',
			}}
		>
			<Box
				component='svg'
				sx={{
					position: 'absolute',
					inset: 0,
				}}
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
			>
				<defs>
					{arcSegments.map(segment => (
						<linearGradient
							key={segment.gradientId}
							id={segment.gradientId}
							x1={segment.gradientCoords.x1}
							y1={segment.gradientCoords.y1}
							x2={segment.gradientCoords.x2}
							y2={segment.gradientCoords.y2}
							gradientUnits='userSpaceOnUse'
						>
							<stop stopColor={segment.colors.start} />
							<stop
								offset='1'
								stopColor={segment.colors.end}
							/>
						</linearGradient>
					))}
				</defs>
				<circle
					cx={center}
					cy={center}
					r={radius}
					stroke='#E6ECF5'
					strokeWidth={resolvedThickness}
					fill='none'
				/>
				{arcSegments.map(segment => (
					<path
						key={segment.gradientId}
						d={segment.path}
						stroke={`url(#${segment.gradientId})`}
						strokeWidth={resolvedThickness}
						strokeLinecap='round'
						fill='none'
					/>
				))}
			</Box>
			<Box
				sx={{
					width: innerSize,
					height: innerSize,
					borderRadius: '50%',
					backgroundColor: '#FFFFFF',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexDirection: 'column',
					gap: '2px',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 700,
						fontSize: '20px',
						color: 'var(--primary-dark)',
						lineHeight: 1,
					}}
				>
					{value}
				</Typography>
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 500,
						fontSize: '11px',
						color: 'var(--primary-light)',
						lineHeight: 1,
					}}
				>
					/{total}
				</Typography>
			</Box>
		</Box>
	)
}

const StatusLegendItem = ({ color, label, value }: { color: string; label: string; value: number }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
		<Box
			sx={{
				width: 18,
				height: 18,
				borderRadius: '50%',
				backgroundColor: color,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				color: '#FFFFFF',
				fontSize: '11px',
				fontWeight: 700,
			}}
		>
			{value}
		</Box>
		<Typography
			sx={{
				fontFamily: 'var(--font-family)',
				fontWeight: 500,
				fontSize: '12px',
				color: 'var(--primary-dark)',
			}}
		>
			{label}
		</Typography>
	</Box>
)

const SchedulesDriverPage = React.memo(function SchedulesDriverPage() {
	const { id } = useParams()
	const { openModal } = useModal()
	const drivers = useMemo(() => driversMock as Driver[], [])
	const defaultDriver = drivers[0]
	const driver = drivers.find(item => String(item.id) === String(id)) ?? defaultDriver
	const [selectedDriverId, setSelectedDriverId] = useState(String(driver?.id ?? defaultDriver?.id ?? ''))
	const [tab, setTab] = useState(0)
	const [statusVisible, setStatusVisible] = useState(false)
	const [alertsTab, setAlertsTab] = useState(0)
	const [serviceTableDensity, setServiceTableDensity] = useState<'compact' | 'standard'>('compact')
	const [folgasTableDensity, setFolgasTableDensity] = useState<'compact' | 'standard'>('compact')
	const [calendarDialogOpen, setCalendarDialogOpen] = useState(false)
	const [calendarTab, setCalendarTab] = useState(0)
	const [calendarDensity, setCalendarDensity] = useState<'compact' | 'standard'>('compact')
	const [consultDrawerOpen, setConsultDrawerOpen] = useState(false)
	const [as400DialogOpen, setAs400DialogOpen] = useState(false)
	const [servicesTotalsDialogOpen, setServicesTotalsDialogOpen] = useState(false)
	const [servicesTotalsDensity, setServicesTotalsDensity] = useState<'compact' | 'standard'>('compact')
	const [driverSituationsDialogOpen, setDriverSituationsDialogOpen] = useState(false)
	const [driverSituationsDensity, setDriverSituationsDensity] = useState<'compact' | 'standard'>('compact')
	const [createSituationOpen, setCreateSituationOpen] = useState(false)
	const [driverSituations, setDriverSituations] = useState<DriverSituationRow[]>(driverSituationsGridRows)
	const [selectedDriverSituations, setSelectedDriverSituations] = useState<Array<string | number>>([])
	const [sinistralidadeDialogOpen, setSinistralidadeDialogOpen] = useState(false)
	const [sinistralidadeDensity, setSinistralidadeDensity] = useState<'compact' | 'standard'>('compact')
	const [activityDeclarationDialogOpen, setActivityDeclarationDialogOpen] = useState(false)
	const [absentismoDialogOpen, setAbsentismoDialogOpen] = useState(false)
	const [absentismoDensity, setAbsentismoDensity] = useState<'compact' | 'standard'>('compact')
	const [restAfterWorkDialogOpen, setRestAfterWorkDialogOpen] = useState(false)
	const [restAfterWorkDensity, setRestAfterWorkDensity] = useState<'compact' | 'standard'>('compact')
	const [newSituation, setNewSituation] = useState({
		dataInicial: '15/08/2024',
		dataFinal: '15/08/2024',
		situacao: '09 - Outras',
		folga: 'Segunda-Feira',
		complementar: 'Terça-Feira',
		observacoes: 'Lorem Impsum',
		anoFerias: '2025',
		semVencimento: true,
		contarDiasTrabalho: false,
		entregouJustificativo: true,
		dataEntrega: '16/08/2024',
	})
	const [tachoDialogOpen, setTachoDialogOpen] = useState(false)
	const [tachoDensity, setTachoDensity] = useState<'compact' | 'standard'>('compact')
	const [selectedEscalas, setSelectedEscalas] = useState<string[]>(['1', '2'])

	const allEscalasSelected = selectedEscalas.length === serviceEscalasRows.length
	const someEscalasSelected = selectedEscalas.length > 0 && !allEscalasSelected
	const tableCellPadding = serviceTableDensity === 'compact' ? '4px 16px' : '8px 16px'
	const calendarCellPadding = calendarDensity === 'compact' ? '4px 12px' : '8px 12px'
	const servicesTotalsCellPadding = servicesTotalsDensity === 'compact' ? '6px 12px' : '10px 12px'
	const driverSituationsCellPadding = driverSituationsDensity === 'compact' ? '6px 12px' : '10px 12px'
	const sinistralidadeCellPadding = sinistralidadeDensity === 'compact' ? '6px 12px' : '10px 12px'
	const restAfterWorkCellPadding = restAfterWorkDensity === 'compact' ? '6px 12px' : '10px 12px'
	const tachoCellPadding = tachoDensity === 'compact' ? '6px 12px' : '10px 12px'
	const driverSituationsTotalDays = useMemo(
		() =>
			driverSituations.reduce((total, row) => {
				const parsed = Number(row.dias)
				return Number.isFinite(parsed) ? total + parsed : total
			}, 0),
		[driverSituations],
	)
	const servicesTotalsGridSx = useMemo(
		() => ({
			...dataGridBaseSx,
			'& .MuiDataGrid-columnHeader': {
				padding: servicesTotalsCellPadding,
			},
			'& .MuiDataGrid-cell': {
				padding: servicesTotalsCellPadding,
				display: 'flex',
				alignItems: 'center',
			},
			'& .MuiDataGrid-row.row--alert': {
				backgroundColor: 'rgba(255, 232, 216, 0.6)',
			},
			'& .MuiDataGrid-row.row--warning': {
				backgroundColor: 'rgba(255, 243, 226, 0.7)',
			},
		}),
		[servicesTotalsCellPadding],
	)
	const servicesTotalsByTypeGridSx = useMemo(
		() => ({
			...dataGridBaseSx,
			'& .MuiDataGrid-columnHeader': {
				padding: servicesTotalsCellPadding,
			},
			'& .MuiDataGrid-cell': {
				padding: servicesTotalsCellPadding,
				display: 'flex',
				alignItems: 'center',
			},
			'& .MuiDataGrid-row.row--totals': {
				backgroundColor: 'rgba(245, 247, 252, 0.5)',
			},
		}),
		[servicesTotalsCellPadding],
	)
	const driverSituationsGridSx = useMemo(
		() => ({
			...dataGridBaseSx,
			'& .MuiDataGrid-columnHeader': {
				padding: driverSituationsCellPadding,
			},
			'& .MuiDataGrid-cell': {
				padding: driverSituationsCellPadding,
				display: 'flex',
				alignItems: 'center',
			},
			'& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
				paddingLeft: '8px',
				paddingRight: '8px',
			},
		}),
		[driverSituationsCellPadding],
	)
	const statusDashboard = useMemo(() => statusDashboardMock as StatusDashboardData, [])

	const handleToggleEscala = (id: string) => {
		setSelectedEscalas(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]))
	}

	const handleToggleAllEscalas = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		setSelectedEscalas(checked ? serviceEscalasRows.map(row => row.id) : [])
	}

	const handleOpenCalendar = () => {
		setCalendarDialogOpen(true)
	}

	const handleCloseCalendar = () => {
		setCalendarDialogOpen(false)
	}

	const handleOpenConsultDrawer = () => {
		setConsultDrawerOpen(true)
	}

	const handleCloseConsultDrawer = () => {
		setConsultDrawerOpen(false)
	}

	const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setStatusVisible(event.target.checked)
	}

	const handleOpenAs400 = () => {
		setAs400DialogOpen(true)
	}

	const handleCloseAs400 = () => {
		setAs400DialogOpen(false)
	}

	const openDriverServiceTotalsModal = (footerVariant: DriverServiceTotalsModalData['footerVariant'] = 'export') => {
		const modalData: DriverServiceTotalsModalData = {
			driverId: selectedDriverId,
			startDate: '02/05/2024',
			endDate: '08/05/2024',
			filial: 'Transol',
			driver: driver?.name ?? 'Alexandre',
			includeScheduleTime: true,
			footerVariant,
			driverCost: '0,00',
			services: servicesTotalsRows,
			totals: servicesTotalsByTypeRows,
			stats: {
				kmsCarga: '1890',
				kmsVazio: '246',
				totalValue: '930,90',
			},
		}

		openModal('Servicos e Totais por Motorista', modalData, [])
	}

	const handleOpenServicesTotals = () => {
		openDriverServiceTotalsModal()
	}

	const handleCloseServicesTotals = () => {
		setServicesTotalsDialogOpen(false)
	}

	const handleOpenDriverSituations = () => {
		setDriverSituationsDialogOpen(true)
	}

	const handleCloseDriverSituations = () => {
		setDriverSituationsDialogOpen(false)
		setCreateSituationOpen(false)
		setSelectedDriverSituations([])
	}

	const handleOpenSinistralidade = () => {
		setSinistralidadeDialogOpen(true)
	}

	const handleCloseSinistralidade = () => {
		setSinistralidadeDialogOpen(false)
	}

	const handleOpenActivityDeclaration = () => {
		setActivityDeclarationDialogOpen(true)
	}

	const handleCloseActivityDeclaration = () => {
		setActivityDeclarationDialogOpen(false)
	}

	const handleOpenServicesTotalsFromActivity = () => {
		setActivityDeclarationDialogOpen(false)
		openDriverServiceTotalsModal()
	}

	const handleOpenAbsentismo = () => {
		setActivityDeclarationDialogOpen(false)
		setAbsentismoDialogOpen(true)
	}

	const handleCloseAbsentismo = () => {
		setAbsentismoDialogOpen(false)
	}

	const handleOpenRestAfterWork = () => {
		setRestAfterWorkDialogOpen(true)
	}

	const handleCloseRestAfterWork = () => {
		setRestAfterWorkDialogOpen(false)
	}

	const handleToggleCreateSituation = () => {
		setCreateSituationOpen(prev => !prev)
	}

	const handleCloseCreateSituation = () => {
		setCreateSituationOpen(false)
	}

	const handleAddSituation = () => {
		const nextRegistryId = String(70000 + driverSituations.length + 1)
		setDriverSituations(prev => [
			...prev,
			{
				id: `${Date.now()}`,
				inicio: newSituation.dataInicial,
				fim: newSituation.dataFinal,
				dias: '0',
				idSituacao: '9',
				situacao: newSituation.situacao,
				descricao: newSituation.observacoes || '—',
				idRegisto: nextRegistryId,
				idRegistoOk: nextRegistryId.padStart(9, '0'),
				utilizador: driver?.name ?? 'Operador',
				dataRegisto: newSituation.dataEntrega || newSituation.dataInicial,
			},
		])
	}

	const handleOpenTacho = () => {
		setTachoDialogOpen(true)
	}

	const handleCloseTacho = () => {
		setTachoDialogOpen(false)
	}

	const handleShowDadosPessoais = () => {
		setTab(0)
		setConsultDrawerOpen(false)
		setCalendarDialogOpen(false)
		setAs400DialogOpen(false)
		setServicesTotalsDialogOpen(false)
		setDriverSituationsDialogOpen(false)
		setCreateSituationOpen(false)
		setSinistralidadeDialogOpen(false)
		setActivityDeclarationDialogOpen(false)
		setAbsentismoDialogOpen(false)
		setRestAfterWorkDialogOpen(false)
		setTachoDialogOpen(false)
	}

	const handleShowFolgasESaldos = () => {
		setTab(3)
		setConsultDrawerOpen(false)
		setCalendarDialogOpen(false)
		setAs400DialogOpen(false)
		setServicesTotalsDialogOpen(false)
		setDriverSituationsDialogOpen(false)
		setCreateSituationOpen(false)
		setSinistralidadeDialogOpen(false)
		setActivityDeclarationDialogOpen(false)
		setAbsentismoDialogOpen(false)
		setRestAfterWorkDialogOpen(false)
		setTachoDialogOpen(false)
	}

	const renderCalendarCell = (cell: CalendarCell) => {
		if (!cell) {
			return null
		}
		if (typeof cell === 'string') {
			return <Typography sx={tableCellTextSx}>{cell}</Typography>
		}
		const colorMap = {
			blue: { border: '#00A5FF', text: '#00A5FF', bg: 'rgba(0, 165, 255, 0.08)' },
			green: { border: '#13A574', text: '#13A574', bg: 'rgba(19, 165, 116, 0.1)' },
			orange: { border: '#FF7A00', text: '#FF7A00', bg: 'rgba(255, 122, 0, 0.12)' },
		} as const
		const colors = colorMap[cell.color]
		return (
			<Box
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					borderRadius: '999px',
					border: `1px solid ${colors.border}`,
					padding: '2px 8px',
					backgroundColor: colors.bg,
				}}
			>
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '11px',
						fontWeight: 500,
						lineHeight: 1.3,
						color: colors.text,
						whiteSpace: 'nowrap',
					}}
				>
					{cell.label}
				</Typography>
			</Box>
		)
	}

	const serviceStats = statusDashboard.services
	const coverageStats = statusDashboard.coverage
	const driverStats = statusDashboard.drivers
	const vehicleStats = statusDashboard.vehicles

	const scheduledServicesCount = serviceStats.escalados + serviceStats.subcontracted
	const servicesSegments: DonutSegment[] = [
		{
			value: serviceStats.escalados,
			colorStart: donutGradients.green.start,
			colorEnd: donutGradients.green.end,
			gradientKey: 'green',
		},
		{
			value: serviceStats.subcontracted,
			colorStart: donutGradients.gray.start,
			colorEnd: donutGradients.gray.end,
			gradientKey: 'gray',
		},
		{
			value: serviceStats.porEscalar,
			colorStart: donutGradients.orange.start,
			colorEnd: donutGradients.orange.end,
			gradientKey: 'orange',
		},
	]
	const servicesMainSegments: DonutSegment[] = [
		{
			value: scheduledServicesCount,
			colorStart: donutGradients.green.start,
			colorEnd: donutGradients.green.end,
			gradientKey: 'green',
		},
	]

	const coverageSegments: DonutSegment[] = [
		{
			value: coverageStats.covered,
			colorStart: donutGradients.blue.start,
			colorEnd: donutGradients.blue.end,
			gradientKey: 'blue',
		},
		{
			value: coverageStats.incomplete,
			colorStart: donutGradients.orange.start,
			colorEnd: donutGradients.orange.end,
			gradientKey: 'orange',
		},
	]

	const driversSegments: DonutSegment[] = [
		{
			value: driverStats.escalados,
			colorStart: donutGradients.green.start,
			colorEnd: donutGradients.green.end,
			gradientKey: 'green',
		},
		{
			value: driverStats.naoEscalados,
			colorStart: donutGradients.orange.start,
			colorEnd: donutGradients.orange.end,
			gradientKey: 'orange',
		},
		{
			value: driverStats.fora,
			colorStart: donutGradients.gray.start,
			colorEnd: donutGradients.gray.end,
			gradientKey: 'gray',
		},
	]

	const vehiclesSegments: DonutSegment[] = [
		{
			value: vehicleStats.escalados,
			colorStart: donutGradients.blue.start,
			colorEnd: donutGradients.blue.end,
			gradientKey: 'blue',
		},
		{
			value: vehicleStats.naoEscalados,
			colorStart: donutGradients.orange.start,
			colorEnd: donutGradients.orange.end,
			gradientKey: 'orange',
		},
		{
			value: vehicleStats.emOficina,
			colorStart: donutGradients.gray.start,
			colorEnd: donutGradients.gray.end,
			gradientKey: 'gray',
		},
	]

	const servicesAlertText = `Existem ${serviceStats.missingDriver} Serviços sem Motorista e ${serviceStats.missingVehicle} Sem Viatura.`

	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				height: '100%',
				overflow: 'hidden',
				flex: 1,
				minWidth: 0,
			}}
		>
			<Breadcrumbs
				aria-label='breadcrumb'
				separator={<Typography sx={{ ...breadcrumbLinkSx, color: '#C7D1E0' }}>/</Typography>}
				sx={{
					width: '100%',
					height: '40px',
					display: 'flex',
					alignItems: 'center',
					'& .MuiBreadcrumbs-ol': {
						alignItems: 'center',
					},
					'& .MuiBreadcrumbs-separator': {
						margin: '0 6px',
					},
				}}
			>
				<Link
					underline='none'
					href='/'
					sx={breadcrumbLinkSx}
				>
					Home
				</Link>
				<Link
					underline='none'
					href='/schedules'
					sx={breadcrumbLinkSx}
				>
					Escalas
				</Link>
				<br />
				<Typography sx={breadcrumbCurrentSx}>Ficha Motorista</Typography>
			</Breadcrumbs>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '16px',
					flexWrap: 'wrap',
					paddingBottom: '16px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
						gap: '8px',
					}}
				>
					<CustomButton
						variant='text'
						size='small'
						href='/schedules'
						sx={{
							color: '#00A5FF',
							fontWeight: 600,
							fontSize: '14px',
							textTransform: 'none',
							padding: '4px 0',
							minWidth: 'unset',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
							<KeyboardArrowLeftRoundedIcon sx={{ fontSize: 18 }} />
							Escalas
						</Box>
					</CustomButton>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<TextField
							select
							size='small'
							label='Motorista'
							value={selectedDriverId}
							onChange={event => setSelectedDriverId(event.target.value)}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							sx={{
								...headerFieldSx,
								minWidth: '220px',
							}}
						>
							{drivers.map(item => (
								<MenuItem
									key={item.id}
									value={String(item.id)}
								>
									{item.number ?? item.id} - {item.name}
								</MenuItem>
							))}
						</TextField>

						<Chip
							label='Motorista Ativo'
							variant='outlined'
							sx={{
								borderColor: '#13A574',
								color: '#13A574',
								backgroundColor: 'rgba(19, 165, 116, 0.05)',
								fontFamily: 'var(--font-family)',
								fontWeight: 500,
								fontSize: '12px',
								height: 28,
							}}
						/>
						<Chip
							icon={<WarningAmberRoundedIcon sx={{ color: '#FF5C00', fontSize: 18 }} />}
							label='Documentação Expirada'
							variant='outlined'
							sx={{
								borderColor: '#FF5C00',
								color: '#FF5C00',
								backgroundColor: 'rgba(255, 92, 0, 0.05)',
								fontFamily: 'var(--font-family)',
								fontWeight: 500,
								fontSize: '12px',
								height: 28,
								'& .MuiChip-icon': {
									marginLeft: '6px',
									marginRight: '-2px',
								},
							}}
						/>
					</Box>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
					<CustomButton
						variant='text'
						size='small'
						endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
						onClick={handleOpenConsultDrawer}
						sx={{
							color: '#00A5FF',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: '22px',
							textTransform: 'none',
							padding: '4px 6px',
						}}
					>
						Consultar
					</CustomButton>
					<IconButton
						size='small'
						sx={{
							width: 32,
							height: 32,
							border: '1px solid rgba(134, 149, 175, 0.2)',
							backgroundColor: '#FFFFFF',
							'&:hover': { backgroundColor: '#F5F7FC' },
						}}
					>
						<FilterAltOutlinedIcon sx={{ color: '#00A5FF' }} />
					</IconButton>
					<CustomButton
						size='small'
						sx={{
							background: '#00A5FF',
							color: '#FFFFFF',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '6px 16px',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: '24px',
						}}
						endIcon={<KeyboardArrowDownIcon sx={{ color: '#FFFFFF' }} />}
					>
						Enviar Notificação
					</CustomButton>
					<CustomButton
						size='small'
						sx={{
							backgroundImage: 'linear-gradient(90.0826deg, var(--primary-dark) 0%, #00A5FF 99.97%)',
							color: '#FFFFFF',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '6px 16px',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: '24px',
						}}
					>
						Guardar
					</CustomButton>
					<SchedulesStatusSwitch
						checked={statusVisible}
						onChange={handleStatusChange}
					/>
				</Box>
			</Box>

			<Collapse
				in={statusVisible}
				timeout={280}
				unmountOnExit
			>
				<Box
					sx={{
						marginBottom: '24px',
						padding: '16px',
						borderRadius: '16px',
						background: 'linear-gradient(180deg, rgba(0, 165, 255, 0.08) 0%, rgba(0, 165, 255, 0.02) 100%)',
						border: '1px solid rgba(134, 149, 175, 0.15)',
					}}
				>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', xl: '2fr 1fr' },
							gap: '16px',
						}}
					>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Box
								sx={{
									backgroundColor: '#FFFFFF',
									borderRadius: '14px',
									padding: '20px',
									boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										flexWrap: 'wrap',
										gap: '16px',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '20px',
											color: 'var(--primary-dark)',
										}}
									>
										Serviços
									</Typography>
									<Box
										sx={{
											flex: '1 1 360px',
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
											padding: '8px 16px',
											borderRadius: '6px',
											backgroundImage: 'linear-gradient(90deg, #FF7A00 0%, #F2B24B 100%)',
										}}
									>
										<WarningAmberRoundedIcon sx={{ color: '#FFFFFF', fontSize: 18 }} />
										<Typography
											sx={{
												fontFamily: 'var(--font-family)',
												fontWeight: 600,
												fontSize: '13px',
												color: '#FFFFFF',
											}}
										>
											{servicesAlertText}
										</Typography>
									</Box>
								</Box>

								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
										gap: '24px',
										marginTop: '20px',
									}}
								>
									<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
										<Box sx={{ position: 'relative', width: 104, height: 104 }}>
											<DonutChart
												value={scheduledServicesCount}
												total={serviceStats.total}
												segments={servicesMainSegments}
												size={104}
											/>
											<Box
												sx={{
													position: 'absolute',
													right: -6,
													top: '50%',
													transform: 'translateY(-50%)',
													display: 'flex',
													flexDirection: 'column',
													gap: '6px',
												}}
											>
												<Box
													sx={{
														width: 8,
														height: 8,
														borderRadius: '50%',
														backgroundColor: donutGradients.gray.start,
													}}
												/>
												<Box
													sx={{
														width: 8,
														height: 8,
														borderRadius: '50%',
														backgroundColor: donutGradients.orange.start,
													}}
												/>
											</Box>
										</Box>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<Typography
												sx={{
													fontFamily: 'var(--font-family)',
													fontWeight: 600,
													fontSize: '15px',
													color: 'var(--primary-dark)',
												}}
											>
												Escalonamento
											</Typography>
											<StatusLegendItem
												color='#13A574'
												value={serviceStats.escalados}
												label='Escalados'
											/>
											<StatusLegendItem
												color='#93A2BD'
												value={serviceStats.subcontracted}
												label='Subcontratados'
											/>
											<StatusLegendItem
												color='#FF7A00'
												value={serviceStats.porEscalar}
												label='Por Escalar'
											/>
										</Box>
									</Box>

									<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
										<DonutChart
											value={coverageStats.covered}
											total={coverageStats.total}
											segments={coverageSegments}
											size={104}
										/>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<Typography
												sx={{
													fontFamily: 'var(--font-family)',
													fontWeight: 600,
													fontSize: '15px',
													color: 'var(--primary-dark)',
												}}
											>
												Escalados
											</Typography>
											<StatusLegendItem
												color='#00A5FF'
												value={coverageStats.covered}
												label='Cobertos'
											/>
											<StatusLegendItem
												color='#FF5C00'
												value={coverageStats.incomplete}
												label='Incompletos'
											/>
										</Box>
									</Box>
								</Box>
							</Box>

							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
									gap: '16px',
								}}
							>
								<Box
									sx={{
										backgroundColor: '#FFFFFF',
										borderRadius: '14px',
										padding: '20px',
										boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
										display: 'flex',
										flexDirection: 'column',
										gap: '16px',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '20px',
											color: 'var(--primary-dark)',
										}}
									>
										Motoristas
									</Typography>
									<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
										<DonutChart
											value={driverStats.escalados}
											total={driverStats.total}
											segments={driversSegments}
											size={98}
										/>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<StatusLegendItem
												color='#13A574'
												value={driverStats.escalados}
												label='Escalados'
											/>
											<StatusLegendItem
												color='#FF7A00'
												value={driverStats.naoEscalados}
												label='Não Escalados'
											/>
											<StatusLegendItem
												color='#93A2BD'
												value={driverStats.fora}
												label='Fora'
											/>
										</Box>
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
										{driverStats.alerts.map(item => (
											<Box
												key={item.id}
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
													padding: '10px 12px',
													borderRadius: '10px',
													border: '1px solid rgba(134, 149, 175, 0.15)',
												}}
											>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
													<Box
														sx={{
															width: 24,
															height: 24,
															borderRadius: '50%',
															backgroundColor: '#FF7A00',
															color: '#FFFFFF',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontFamily: 'var(--font-family)',
															fontWeight: 600,
															fontSize: '12px',
														}}
													>
														{item.count}
													</Box>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontWeight: 500,
															fontSize: '13px',
															color: 'var(--primary-dark)',
														}}
													>
														{item.label}
													</Typography>
												</Box>
												<ChevronRightRoundedIcon sx={{ color: '#00A5FF' }} />
											</Box>
										))}
									</Box>
								</Box>

								<Box
									sx={{
										backgroundColor: '#FFFFFF',
										borderRadius: '14px',
										padding: '20px',
										boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
										display: 'flex',
										flexDirection: 'column',
										gap: '16px',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '20px',
											color: 'var(--primary-dark)',
										}}
									>
										Viaturas
									</Typography>
									<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
										<DonutChart
											value={vehicleStats.escalados}
											total={vehicleStats.total}
											segments={vehiclesSegments}
											size={98}
										/>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
											<StatusLegendItem
												color='#00A5FF'
												value={vehicleStats.escalados}
												label='Escalados'
											/>
											<StatusLegendItem
												color='#FF7A00'
												value={vehicleStats.naoEscalados}
												label='Não Escalados'
											/>
											<StatusLegendItem
												color='#93A2BD'
												value={vehicleStats.emOficina}
												label='Em Oficina'
											/>
										</Box>
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
										{vehicleStats.alerts.map(item => (
											<Box
												key={item.id}
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
													padding: '10px 12px',
													borderRadius: '10px',
													border: '1px solid rgba(134, 149, 175, 0.15)',
												}}
											>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
													<Box
														sx={{
															width: 24,
															height: 24,
															borderRadius: '50%',
															backgroundColor: '#FF7A00',
															color: '#FFFFFF',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontFamily: 'var(--font-family)',
															fontWeight: 600,
															fontSize: '12px',
														}}
													>
														{item.count}
													</Box>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontWeight: 500,
															fontSize: '13px',
															color: 'var(--primary-dark)',
														}}
													>
														{item.label}
													</Typography>
												</Box>
												<ChevronRightRoundedIcon sx={{ color: '#00A5FF' }} />
											</Box>
										))}
									</Box>
								</Box>
							</Box>
						</Box>

						<Box
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '14px',
								padding: '20px',
								boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
								display: 'flex',
								flexDirection: 'column',
								gap: '16px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '8px',
								}}
							>
								<Tabs
									value={alertsTab}
									onChange={(_, value) => setAlertsTab(value)}
									TabIndicatorProps={{
										style: {
											backgroundColor: '#00A5FF',
											height: '3px',
											borderRadius: '2px',
										},
									}}
									sx={{
										minHeight: '32px',
										'& .MuiTab-root': {
											minHeight: '32px',
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '13px',
											textTransform: 'none',
											color: 'var(--primary-light)',
											padding: '6px 12px',
											'&.Mui-selected': {
												color: '#00A5FF',
											},
										},
									}}
								>
									<Tab label='Alertas' />
									<Tab label='Últimas Ações' />
								</Tabs>
								{alertsTab === 0 && (
									<CustomButton
										variant='text'
										size='small'
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '12px',
											textTransform: 'none',
											padding: 0,
										}}
									>
										Marcar como lidos
									</CustomButton>
								)}
							</Box>

							{alertsTab === 0 ? (
								<>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '14px',
											color: 'var(--primary-dark)',
										}}
									>
										Não Lidos
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
										{statusDashboard.alerts.unread.map(alert => (
											<Box
												key={alert.id}
												sx={{
													display: 'flex',
													gap: '12px',
													padding: '12px 14px',
													borderRadius: '10px',
													backgroundColor: 'rgba(0, 165, 255, 0.08)',
												}}
											>
												<Box
													sx={{
														width: 26,
														height: 26,
														borderRadius: '50%',
														backgroundColor: '#E6F4FF',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
													}}
												>
													<CheckRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
												</Box>
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontWeight: 600,
															fontSize: '13px',
															color: 'var(--primary-dark)',
														}}
													>
														{alert.title}
													</Typography>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontSize: '11px',
															color: 'var(--primary-light)',
														}}
													>
														{alert.timestamp}
													</Typography>
												</Box>
											</Box>
										))}
									</Box>

									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '14px',
											color: 'var(--primary-dark)',
											marginTop: '8px',
										}}
									>
										Lidos
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
										{statusDashboard.alerts.read.map(alert => (
											<Box
												key={alert.id}
												sx={{
													display: 'flex',
													gap: '12px',
													padding: '12px 14px',
													borderRadius: '10px',
													border: '1px solid rgba(134, 149, 175, 0.15)',
												}}
											>
												<Box
													sx={{
														width: 26,
														height: 26,
														borderRadius: '50%',
														backgroundColor: '#F5F7FC',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
													}}
												>
													<CheckRoundedIcon sx={{ color: '#93A2BD', fontSize: 18 }} />
												</Box>
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontWeight: 600,
															fontSize: '13px',
															color: 'var(--primary-dark)',
														}}
													>
														{alert.title}
													</Typography>
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontSize: '11px',
															color: 'var(--primary-light)',
														}}
													>
														{alert.timestamp}
													</Typography>
												</Box>
											</Box>
										))}
									</Box>
								</>
							) : (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									{[...statusDashboard.alerts.unread, ...statusDashboard.alerts.read].map(alert => (
										<Box
											key={alert.id}
											sx={{
												display: 'flex',
												gap: '12px',
												padding: '12px 14px',
												borderRadius: '10px',
												border: '1px solid rgba(134, 149, 175, 0.15)',
											}}
										>
											<Box
												sx={{
													width: 26,
													height: 26,
													borderRadius: '50%',
													backgroundColor: '#F5F7FC',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
												}}
											>
												<CheckRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
											</Box>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
												<Typography
													sx={{
														fontFamily: 'var(--font-family)',
														fontWeight: 600,
														fontSize: '13px',
														color: 'var(--primary-dark)',
													}}
												>
													{alert.title}
												</Typography>
												<Typography
													sx={{
														fontFamily: 'var(--font-family)',
														fontSize: '11px',
														color: 'var(--primary-light)',
													}}
												>
													{alert.timestamp}
												</Typography>
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			</Collapse>

			<Box
				sx={{
					backgroundColor: '#FFFFFF',
					borderRadius: '10px',
					padding: '24px 16px',
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
					flex: 1,
					overflow: 'auto',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
						<Tabs
							value={tab}
							onChange={(_, value) => setTab(value)}
							TabIndicatorProps={{
								style: {
									backgroundColor: '#00A5FF',
									height: '3px',
									borderRadius: '2px',
								},
							}}
							sx={{
								minHeight: '40px',
								width: '100%',
								'& .MuiTabs-flexContainer': {
									justifyContent: 'center',
								},
								'& .MuiTab-root': {
									fontSize: '14px',
									lineHeight: '24px',
									color: 'var(--primary-light)',
									fontWeight: 600,
									textTransform: 'none',
									minHeight: '40px',
									padding: '9px 16px',
									'&.Mui-selected': { color: '#00A5FF' },
								},
							}}
						>
							<Tab label='Dados Pessoais' />
							<Tab label='Documentação' />
							<Tab label='Serviço' />
							<Tab label='Folgas e Saldos' />
						</Tabs>
					</Box>

					{tab === 0 && (
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.334,
								color: 'var(--primary-dark)',
							}}
						>
							Dados do Motorista
						</Typography>
					)}
				</Box>

				{tab === 0 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' },
								gap: '32px',
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
									<Avatar
										sx={{
											borderRadius: '100%',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											bgcolor: 'lightgrey 50% / cover no-repeat',
											width: 32,
											height: 32,
											fontSize: '20px',
											fontWeight: 400,
											lineHeight: 1,
										}}
									>
										{driver?.name?.[0] ?? 'M'}
									</Avatar>
									<CustomButton
										variant='text'
										size='small'
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											lineHeight: '22px',
											textTransform: 'none',
											padding: '4px 0',
										}}
									>
										Adicionar Fotografia +
									</CustomButton>
								</Box>

								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.6fr 0.6fr' },
										gap: '16px',
									}}
								>
									<TextField
										label='Abreviatura / Nome de Ecrã'
										size='small'
										value={driver?.name?.split(' ')[0] ?? ''}
										sx={cardFieldSx}
									/>
									<TextField
										label='Nr Motorista'
										size='small'
										value={driver?.id ?? ''}
										sx={cardFieldSx}
									/>
									<TextField
										label='Nr Mecanográfico'
										size='small'
										value={driver?.number ?? ''}
										sx={cardFieldSx}
									/>
								</Box>

								<TextField
									label='Nome Motorista'
									size='small'
									value={driver?.name ?? ''}
									fullWidth
									sx={cardFieldSx}
								/>

								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: {
											xs: '1fr',
											md: '1fr 1fr 1fr 0.6fr',
										},
										gap: '16px',
									}}
								>
									<TextField
										label='Nacionalidade'
										size='small'
										value='Portuguesa'
										sx={cardFieldSx}
									/>
									<TextField
										label='Estado Civil'
										size='small'
										value='Solteiro'
										sx={cardFieldSx}
									/>
									<TextField
										label='Data Nascimento'
										size='small'
										value='05/07/1979'
										sx={cardFieldSx}
									/>
									<TextField
										label='Idade'
										size='small'
										value='46'
										sx={cardFieldSx}
									/>
								</Box>

								<TextField
									label='Comentários e Observações'
									multiline
									minRows={3}
									size='small'
									defaultValue='It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout.'
									fullWidth
									sx={cardFieldSx}
								/>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
								<Typography
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '16px',
										lineHeight: 1.75,
										letterSpacing: '0.15px',
										color: 'var(--primary-dark)',
									}}
								>
									Contactos
								</Typography>

								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
										gap: '16px',
									}}
								>
									<TextField
										label='Email'
										size='small'
										value='aldina@email.com'
										sx={cardFieldSx}
									/>
									<TextField
										label='Email_BCC'
										size='small'
										value='aldina_2@email.com'
										sx={cardFieldSx}
									/>
								</Box>

								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked
											sx={{
												color: 'rgba(134, 149, 175, 0.5)',
												'&.Mui-checked': { color: '#00A5FF' },
											}}
										/>
									}
									label={
										<Typography
											sx={{
												fontFamily: 'var(--font-family)',
												fontSize: '13px',
												fontWeight: 500,
												color: 'var(--primary-dark)',
											}}
										>
											Pode receber escala por email
										</Typography>
									}
								/>

								<TextField
									label='Telefone'
									size='small'
									value={driver?.phone ?? '910000000'}
									sx={cardFieldSx}
								/>
								<TextField
									label='Morada'
									size='small'
									value='Av da Liberdade, nr 35 3º Esq - Lisboa'
									fullWidth
									sx={cardFieldSx}
								/>
								<TextField
									label='Código Postal'
									size='small'
									value='1000-039 - Lisboa'
									sx={cardFieldSx}
								/>
							</Box>
						</Box>
					</Box>
				)}

				{tab === 1 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								Documentação
							</Typography>

							<Box sx={alertBoxSx}>
								<WarningAmberRoundedIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
								<Typography sx={alertTextSx}>Existem documentos com validade expirada.</Typography>
								<CustomButton
									variant='text'
									size='small'
									sx={alertActionButtonSx}
								>
									Enviar Notificação
								</CustomButton>
							</Box>
						</Box>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
								gap: '32px',
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Typography sx={sectionTitleSx}>Cartão de Cidadão e Contribuinte</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr Cartão de Cidadão'
											size='small'
											value='00000000'
											sx={cardFieldSx}
										/>
										<TextField
											label='Emissão'
											size='small'
											value='10/02/2020'
											sx={cardFieldSx}
										/>
										<TextField
											label='Validade'
											size='small'
											value='10/02/2030'
											sx={cardFieldSx}
										/>
									</Box>
									<TextField
										label='Nr Contribuinte'
										size='small'
										value='000000000'
										sx={{ ...cardFieldSx, maxWidth: { xs: '100%', md: '200px' } }}
									/>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: '12px',
										}}
									>
										<Typography sx={sectionTitleSx}>Passaporte</Typography>
										<FormControlLabel
											control={
												<Checkbox
													size='small'
													sx={checkboxSx}
												/>
											}
											label={<Typography sx={checkboxLabelSx}>Tem Passaporte</Typography>}
										/>
									</Box>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr Passaporte'
											size='small'
											value=''
											disabled
											InputLabelProps={{ shrink: true }}
											sx={disabledCardFieldSx}
										/>
										<TextField
											label='Emissão'
											size='small'
											value=''
											disabled
											InputLabelProps={{ shrink: true }}
											sx={disabledCardFieldSx}
										/>
										<TextField
											label='Validade'
											size='small'
											value=''
											disabled
											InputLabelProps={{ shrink: true }}
											sx={disabledCardFieldSx}
										/>
									</Box>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Typography sx={sectionTitleSx}>Carta de Condução</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
											gap: '16px',
										}}
									>
										<TextField
											select
											label='Tipo de Carta'
											size='small'
											value='Pesados'
											SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
											sx={cardFieldSx}
										>
											<MenuItem value='Pesados'>Pesados</MenuItem>
											<MenuItem value='Ligeiros'>Ligeiros</MenuItem>
										</TextField>
										<TextField
											label='Direção de Viação'
											size='small'
											value='Faro'
											sx={cardFieldSx}
										/>
									</Box>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr Carta de Condução'
											size='small'
											value='00000000'
											sx={cardFieldSx}
										/>
										<TextField
											label='Emissão'
											size='small'
											value='10/02/2020'
											sx={cardFieldSx}
										/>
										<TextField
											label='Validade'
											size='small'
											value='10/02/2030'
											sx={cardFieldSx}
										/>
									</Box>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Typography sx={sectionTitleSx}>Certificado Aptidão Motorista (CAM)</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr CAM'
											size='small'
											value='00000000'
											sx={cardFieldSx}
										/>
										<Tooltip
											title='Validade Expirada'
											placement='top'
											arrow
											slotProps={{
												tooltip: {
													sx: {
														fontFamily: 'var(--font-family)',
														fontSize: '10px',
														lineHeight: '14px',
													},
												},
											}}
										>
											<Box>
												<TextField
													label='Validade'
													size='small'
													value='10/02/2020'
													sx={expiredCardFieldSx}
												/>
											</Box>
										</Tooltip>
									</Box>
								</Box>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Typography sx={sectionTitleSx}>Cartão de Motorista</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr Cartão de Motorista'
											size='small'
											value='00000000'
											sx={cardFieldSx}
										/>
										<TextField
											label='Validade'
											size='small'
											value='10/02/2030'
											sx={cardFieldSx}
										/>
									</Box>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: '12px',
										}}
									>
										<Typography sx={sectionTitleSx}>Cartão Europeu Seguro Doença (CESD)</Typography>
										<FormControlLabel
											control={
												<Checkbox
													size='small'
													checked
													sx={checkboxSx}
												/>
											}
											label={<Typography sx={checkboxLabelSx}>Tem CESD</Typography>}
										/>
									</Box>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
											gap: '16px',
										}}
									>
										<TextField
											label='Nr CESD'
											size='small'
											value='00000000'
											sx={cardFieldSx}
										/>
										<TextField
											label='Validade'
											size='small'
											value='10/02/2020'
											sx={expiredCardFieldSx}
										/>
									</Box>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: '12px',
											flexWrap: 'wrap',
										}}
									>
										<Typography sx={sectionTitleSx}>Licença para Transporte de Crianças</Typography>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
											<FormControlLabel
												control={
													<Checkbox
														size='small'
														sx={checkboxSx}
													/>
												}
												label={<Typography sx={checkboxLabelSx}>Tem Licença</Typography>}
											/>
											<FormControlLabel
												control={
													<Checkbox
														size='small'
														checked
														sx={checkboxSx}
													/>
												}
												label={<Typography sx={checkboxLabelSx}>Nunca terá Licença</Typography>}
											/>
										</Box>
									</Box>
									<TextField
										label='Validade'
										size='small'
										value=''
										disabled
										InputLabelProps={{ shrink: true }}
										sx={disabledCardFieldSx}
									/>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: '12px',
										}}
									>
										<Typography sx={sectionTitleSx}>Registo Criminal</Typography>
										<FormControlLabel
											control={
												<Checkbox
													size='small'
													sx={checkboxSx}
												/>
											}
											label={<Typography sx={checkboxLabelSx}>Tem Registo Criminal</Typography>}
										/>
									</Box>
									<TextField
										label='Validade'
										size='small'
										value=''
										disabled
										InputLabelProps={{ shrink: true }}
										sx={disabledCardFieldSx}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				)}

				{tab === 2 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '24px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.334,
								color: 'var(--primary-dark)',
							}}
						>
							Dados de Serviço
						</Typography>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
								gap: '32px',
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
								<Typography sx={sectionTitleSx}>Contratação</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
										gap: '16px',
									}}
								>
									<TextField
										label='Nome da Empresa'
										size='small'
										value='Transol - Transportes e Turismo SA'
										sx={cardFieldSx}
									/>
									<TextField
										label='Data de Admissão'
										size='small'
										value='01/03/2023'
										sx={cardFieldSx}
									/>
								</Box>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
										gap: '16px',
									}}
								>
									<TextField
										select
										label='Contrato'
										size='small'
										value='Antrop'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Antrop'>Antrop</MenuItem>
										<MenuItem value='Outro'>Outro</MenuItem>
									</TextField>
									<TextField
										label='Código Contrato'
										size='small'
										value='74'
										sx={cardFieldSx}
									/>
									<TextField
										select
										label='Subtipo Contrato'
										size='small'
										value='Barraqueiro'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Barraqueiro'>Barraqueiro</MenuItem>
										<MenuItem value='Outro'>Outro</MenuItem>
									</TextField>
								</Box>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												checked
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Entra na Escala de Turismo</Typography>}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Está Inativo</Typography>}
									/>
								</Box>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<Typography sx={{ ...checkboxLabelSx, fontWeight: 600 }}>Sub-Contratação:</Typography>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												checked
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>É Sub-Contratado</Typography>}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Será apenas sub-contratado pela Comercial</Typography>}
									/>
								</Box>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
								<Typography sx={sectionTitleSx}>Processamento de Salários</Typography>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Não tem processamento através desta filial</Typography>}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={
											<Typography sx={checkboxLabelSx}>
												Motorista graduado (influencia o valor dos códigos de refeição)
											</Typography>
										}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={
											<Typography sx={checkboxLabelSx}>
												Trata-se de um assistente, não tem direito a agente único
											</Typography>
										}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={
											<Typography sx={checkboxLabelSx}>Motorista Virtual (serve apenas para gerar chapas)</Typography>
										}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Recebe folha de ponto por App no telemóvel</Typography>}
									/>
								</Box>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
										gap: '16px',
										alignItems: 'end',
									}}
								>
									<TextField
										select
										label='Sindicato'
										size='small'
										value='Nome do Sindicato'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Nome do Sindicato'>Nome do Sindicato</MenuItem>
									</TextField>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
											flexWrap: 'wrap',
										}}
									>
										<Typography
											sx={{
												fontFamily: 'var(--font-family)',
												fontWeight: 600,
												fontSize: '14px',
												lineHeight: 1.57,
												color: 'var(--primary-dark)',
											}}
										>
											Duração Normal de Trabalho:
										</Typography>
										<TextField
											size='small'
											value='08:00'
											sx={{ ...cardFieldSx, maxWidth: '110px' }}
										/>
									</Box>
								</Box>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography sx={sectionTitleSx}>Zonas</Typography>
							<TextField
								select
								label='Zona a que Pertence'
								size='small'
								value='001 - Portimão'
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								sx={{ ...cardFieldSx, maxWidth: { xs: '100%', md: '420px' } }}
							>
								<MenuItem value='001 - Portimão'>001 - Portimão</MenuItem>
							</TextField>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '12px',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>Escalas em que participa</Typography>
								<ToggleButtonGroup
									value={serviceTableDensity}
									exclusive
									onChange={(_, value) => value && setServiceTableDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: serviceTableDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: serviceTableDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Checkbox
													size='small'
													indeterminate={someEscalasSelected}
													checked={allEscalasSelected}
													onChange={handleToggleAllEscalas}
													sx={checkboxSx}
												/>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Centro de Escalas</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Tipo Escala</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>ID Tipo Escala</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Id Centro Escala</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>User Pode Alterar</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>ID Filial</Typography>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{serviceEscalasRows.map(row => {
											const isChecked = selectedEscalas.includes(row.id)
											return (
												<TableRow
													key={row.id}
													sx={{ backgroundColor: isChecked ? '#F5F7FC' : '#FFFFFF' }}
												>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Checkbox
															size='small'
															checked={isChecked}
															onChange={() => handleToggleEscala(row.id)}
															sx={checkboxSx}
														/>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.center}</Typography>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.type}</Typography>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.typeId}</Typography>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.centerId}</Typography>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.canChange}</Typography>
													</TableCell>
													<TableCell sx={{ padding: tableCellPadding }}>
														<Typography sx={tableCellTextSx}>{row.filial}</Typography>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '12px',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>
									Não pode trabalhar com os seguintes Clientes
								</Typography>
								<ToggleButtonGroup
									value={serviceTableDensity}
									exclusive
									onChange={(_, value) => value && setServiceTableDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: serviceTableDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: serviceTableDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Nome Cliente</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>ID Cliente</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Localidade</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Contribuinte</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Data Real</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Ut Windows</Typography>
											</TableCell>
											<TableCell sx={{ padding: tableCellPadding }}>
												<Typography sx={tableHeaderTextSx}>Ut App</Typography>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{serviceBlockedClientsRows.map(row => (
											<TableRow key={row.id}>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.name}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.idCliente}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.localidade}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.contribuinte}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.dataReal}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.utWindows}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.utApp}</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					</Box>
				)}

				{tab === 3 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '24px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.334,
								color: 'var(--primary-dark)',
							}}
						>
							Folgas Atrasadas e Saldos
						</Typography>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>Folgas e Complementares</Typography>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									gap: '16px',
									justifyContent: 'space-between',
								}}
							>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<TextField
										label='Folga'
										size='small'
										value='Segunda-Feira'
										sx={{ ...cardFieldSx, minWidth: { xs: '100%', sm: '198px' } }}
									/>
									<TextField
										label='Complementar'
										size='small'
										value='Terça-Feira'
										sx={{ ...cardFieldSx, minWidth: { xs: '100%', sm: '198px' } }}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Folgas Rotativas</Typography>}
									/>
								</Box>
								<CustomButton
									size='small'
									onClick={handleOpenCalendar}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 16px',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '24px',
									}}
								>
									Calendário Folgas
								</CustomButton>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									flexWrap: 'wrap',
									gap: '12px',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>Folgas Atrasadas</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
									<CustomButton
										variant='text'
										size='small'
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											textTransform: 'none',
											padding: '4px 0',
										}}
									>
										Pagar Folgas +
									</CustomButton>
									<ToggleButtonGroup
										value={folgasTableDensity}
										exclusive
										onChange={(_, value) => value && setFolgasTableDensity(value)}
										size='small'
										sx={tableDensityToggleSx}
									>
										<ToggleButton
											value='compact'
											aria-label='densidade compacta'
										>
											<TableRowsIcon
												sx={{
													width: '14.4px',
													height: '14.4px',
													color: folgasTableDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
												}}
											/>
										</ToggleButton>
										<ToggleButton
											value='standard'
											aria-label='densidade espaçada'
										>
											<ViewHeadlineIcon
												sx={{
													width: '14.4px',
													height: '14.4px',
													color: folgasTableDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
												}}
											/>
										</ToggleButton>
									</ToggleButtonGroup>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
								<TextField
									label='Data Inicial'
									size='small'
									value='01/01/2024'
									sx={{ ...cardFieldSx, maxWidth: '140px' }}
								/>
								<Typography sx={tableCellTextSx}>—</Typography>
								<TextField
									label='Data Final'
									size='small'
									value='31/12/2024'
									sx={{ ...cardFieldSx, maxWidth: '140px' }}
								/>
								<CustomButton
									variant='text'
									size='small'
									sx={{
										color: '#00A5FF',
										fontWeight: 600,
										fontSize: '12px',
										textTransform: 'none',
										marginLeft: 'auto',
										padding: '4px 0',
									}}
								>
									Limpar Filtros
								</CustomButton>
							</Box>
							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
											{[
												'Data Mov',
												'Tipo',
												'Descrição',
												'Folgas',
												'Folgas DB',
												'Saldo',
												'ID Folgas Config',
												'Cód Mov',
											].map(label => (
												<TableCell
													key={label}
													sx={{ padding: tableCellPadding }}
												>
													<Typography sx={tableHeaderTextSx}>{label}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{folgasAtrasadasRows.map(row => (
											<TableRow key={row.id}>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.dataMov}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.tipo}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.descricao}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.folgas}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.folgasDb}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.saldo}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.idConfig}</Typography>
												</TableCell>
												<TableCell sx={{ padding: tableCellPadding }}>
													<Typography sx={tableCellTextSx}>{row.codMov}</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '12px',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>Saldos de Folgas Atrasadas</Typography>
								<ToggleButtonGroup
									value={folgasTableDensity}
									exclusive
									onChange={(_, value) => value && setFolgasTableDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: folgasTableDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: folgasTableDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
											{['Número', 'Nome', 'Nome Escala', 'Saldo', 'Folga', 'Complementar'].map(label => (
												<TableCell
													key={label}
													sx={{ padding: tableCellPadding }}
												>
													<Typography sx={tableHeaderTextSx}>{label}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={6}
												sx={{ padding: 0 }}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: '8px',
														padding: '8px 16px',
														backgroundImage: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
														color: '#FFFFFF',
														borderRadius: '4px',
														margin: '8px',
													}}
												>
													<InfoOutlinedIcon sx={{ fontSize: 18 }} />
													<Typography
														sx={{
															fontFamily: 'var(--font-family)',
															fontSize: '14px',
															fontWeight: 500,
															lineHeight: 1.5,
															letterSpacing: '0.15px',
															color: '#FFFFFF',
														}}
													>
														Sem informação a apresentar.
													</Typography>
												</Box>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</Box>
						</Box>
					</Box>
				)}
			</Box>

			<Dialog
				open={calendarDialogOpen}
				onClose={handleCloseCalendar}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '8px',
						padding: '20px 24px',
					},
				}}
				BackdropProps={{
					sx: {
						backgroundColor: 'rgba(9, 32, 86, 0.2)',
						backdropFilter: 'blur(6px)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 600,
							fontSize: '14px',
							color: 'var(--primary-dark)',
						}}
					>
						Calendário de Folgas Rotativas
					</Typography>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '16px',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
						}}
					>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<TextField
								select
								label='Ano'
								size='small'
								value='2026'
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								sx={{ ...cardFieldSx, minWidth: '120px' }}
							>
								<MenuItem value='2026'>2026</MenuItem>
							</TextField>
							<TextField
								select
								label='Mês'
								size='small'
								value='Janeiro'
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								sx={{ ...cardFieldSx, minWidth: '140px' }}
							>
								<MenuItem value='Janeiro'>Janeiro</MenuItem>
							</TextField>
							<TextField
								select
								label='Grupo'
								size='small'
								value='Folgas Fixas'
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								sx={{ ...cardFieldSx, minWidth: '160px' }}
							>
								<MenuItem value='Folgas Fixas'>Folgas Fixas</MenuItem>
							</TextField>
						</Box>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								padding: '4px 0',
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr auto 1fr',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<Box />
						<Tabs
							value={calendarTab}
							onChange={(_, value) => setCalendarTab(value)}
							TabIndicatorProps={{
								style: {
									backgroundColor: '#00A5FF',
									height: '2px',
									borderRadius: '2px',
								},
							}}
							sx={{
								minHeight: '32px',
								'& .MuiTabs-flexContainer': {
									justifyContent: 'center',
								},
								'& .MuiTab-root': {
									fontSize: '12px',
									color: 'var(--primary-light)',
									fontWeight: 600,
									textTransform: 'none',
									minHeight: '32px',
									padding: '6px 12px',
									'&.Mui-selected': { color: '#00A5FF' },
								},
							}}
						>
							<Tab label='Calendário' />
							<Tab label='Elementos pertencentes ao Grupo' />
						</Tabs>
						<Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
							<CustomButton
								size='small'
								onClick={handleShowDadosPessoais}
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '4px 12px',
									fontWeight: 600,
									fontSize: '13px',
									lineHeight: '22px',
								}}
							>
								Plano Mensal
							</CustomButton>
							<CustomButton
								size='small'
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '4px 12px',
									fontWeight: 600,
									fontSize: '13px',
									lineHeight: '22px',
								}}
							>
								Anual Rotativas
							</CustomButton>
						</Box>
					</Box>

					{calendarTab === 0 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '18px' }}>Mês Selecionado</Typography>
								<ToggleButtonGroup
									value={calendarDensity}
									exclusive
									onChange={(_, value) => value && setCalendarDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: calendarDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: calendarDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow>
											{calendarColumnsSelected.map(label => (
												<TableCell
													key={label}
													sx={{ padding: calendarCellPadding }}
												>
													<Typography sx={tableHeaderTextSx}>{label}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedMonthRows.map((row, index) => (
											<TableRow key={`selected-${index}`}>
												{row.map((cell, cellIndex) => (
													<TableCell
														key={cellIndex}
														sx={{ padding: calendarCellPadding }}
													>
														{renderCalendarCell(cell)}
													</TableCell>
												))}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>

							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
									gap: '24px',
								}}
							>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<Typography sx={{ ...sectionTitleSx, fontSize: '18px' }}>Mês Anterior</Typography>
									<Box
										sx={{
											border: '1px solid rgba(134, 149, 175, 0.15)',
											borderRadius: '6px',
											overflow: 'hidden',
										}}
									>
										<Table size='small'>
											<TableHead>
												<TableRow>
													{calendarColumnsDefault.map(label => (
														<TableCell
															key={label}
															sx={{ padding: calendarCellPadding }}
														>
															<Typography sx={tableHeaderTextSx}>{label}</Typography>
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{previousMonthRows.map((row, index) => (
													<TableRow key={`prev-${index}`}>
														{row.map((cell, cellIndex) => (
															<TableCell
																key={cellIndex}
																sx={{ padding: calendarCellPadding }}
															>
																{renderCalendarCell(cell)}
															</TableCell>
														))}
													</TableRow>
												))}
											</TableBody>
										</Table>
									</Box>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<Typography sx={{ ...sectionTitleSx, fontSize: '18px' }}>Mês Seguinte</Typography>
									<Box
										sx={{
											border: '1px solid rgba(134, 149, 175, 0.15)',
											borderRadius: '6px',
											overflow: 'hidden',
										}}
									>
										<Table size='small'>
											<TableHead>
												<TableRow>
													{calendarColumnsDefault.map(label => (
														<TableCell
															key={label}
															sx={{ padding: calendarCellPadding }}
														>
															<Typography sx={tableHeaderTextSx}>{label}</Typography>
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{nextMonthRows.map((row, index) => (
													<TableRow key={`next-${index}`}>
														{row.map((cell, cellIndex) => (
															<TableCell
																key={cellIndex}
																sx={{ padding: calendarCellPadding }}
															>
																{renderCalendarCell(cell)}
															</TableCell>
														))}
													</TableRow>
												))}
											</TableBody>
										</Table>
									</Box>
								</Box>
							</Box>
						</Box>
					)}

					{calendarTab === 1 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '18px' }}>Elementos pertencentes ao Grupo</Typography>
								<ToggleButtonGroup
									value={calendarDensity}
									exclusive
									onChange={(_, value) => value && setCalendarDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: calendarDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: calendarDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
									maxHeight: '420px',
									overflowY: 'auto',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
											{['Nome Escala', 'Nr', 'Nr Mec', 'ID Motorista'].map(label => (
												<TableCell
													key={label}
													sx={{ padding: calendarCellPadding }}
												>
													<Typography sx={tableHeaderTextSx}>{label}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{calendarGroupMembers.map(member => (
											<TableRow key={member.id}>
												<TableCell sx={{ padding: calendarCellPadding }}>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
														<Avatar
															sx={{
																borderRadius: '100%',
																display: 'flex',
																flexDirection: 'column',
																alignItems: 'center',
																justifyContent: 'center',
																bgcolor: 'lightgrey 50% / cover no-repeat',
																width: 32,
																height: 32,
																fontSize: '20px',
																fontWeight: 400,
																lineHeight: 1,
															}}
														>
															{member.name?.[0] ?? 'M'}
														</Avatar>
														<Typography sx={tableCellTextSx}>{member.name}</Typography>
													</Box>
												</TableCell>
												<TableCell sx={{ padding: calendarCellPadding }}>
													<Typography sx={tableCellTextSx}>{member.nr}</Typography>
												</TableCell>
												<TableCell sx={{ padding: calendarCellPadding }}>
													<Typography sx={tableCellTextSx}>{member.nrMec}</Typography>
												</TableCell>
												<TableCell sx={{ padding: calendarCellPadding }}>
													<Typography sx={tableCellTextSx}>{member.driverId}</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}

					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseCalendar}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '4px 0',
							}}
						>
							Fechar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Drawer
				anchor='left'
				open={consultDrawerOpen}
				onClose={handleCloseConsultDrawer}
				transitionDuration={300}
				PaperProps={{
					sx: {
						width: '354px',
						padding: '28px 24px',
						backgroundColor: '#FFFFFF',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-light)',
							}}
						>
							Consultas do Motorista
						</Typography>
						<IconButton
							onClick={handleCloseConsultDrawer}
							size='small'
							sx={{
								border: '2px solid rgba(134, 149, 175, 0.15)',
								width: 36,
								height: 36,
							}}
						>
							<CloseRoundedIcon sx={{ color: '#00A5FF', fontSize: 20 }} />
						</IconButton>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: 1.3,
									color: 'var(--primary-dark)',
								}}
							>
								Consultar
							</Typography>
							<KeyboardArrowUpRoundedIcon sx={{ color: '#00A5FF' }} />
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							{consultItems.map(item => (
								<Box
									key={item}
									component='button'
									type='button'
									onClick={() => {
										if (item === 'AS400') {
											handleOpenAs400()
											return
										}
										if (item === 'Serviços e Totais do Motorista') {
											handleOpenServicesTotals()
											return
										}
										if (item === 'Situações do Motorista') {
											handleOpenDriverSituations()
											return
										}
										if (item === 'Sinistralidade') {
											handleOpenSinistralidade()
											return
										}
										if (item === 'Declarações de Atividade') {
											handleOpenActivityDeclaration()
											return
										}
										if (item === 'Tacógrafo') {
											handleOpenTacho()
											return
										}
										if (item === 'Calendário de Folgas') {
											handleOpenCalendar()
											return
										}
										if (item === 'Descansos após Folga / Complementar Trabalhada') {
											handleOpenRestAfterWork()
											return
										}
										console.log('Consultar:', item)
									}}
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: '100%',
										height: '48px',
										border: 'none',
										borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
										background: 'transparent',
										padding: 0,
										cursor: 'pointer',
										textAlign: 'left',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 500,
											fontSize: '13px',
											lineHeight: 1.3,
											color: 'var(--primary-dark)',
										}}
									>
										{item}
									</Typography>
									<Box
										sx={{
											width: 30,
											height: 30,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<ChevronRightRoundedIcon sx={{ color: '#00A5FF', fontSize: 20 }} />
									</Box>
								</Box>
							))}
						</Box>
					</Box>
				</Box>
			</Drawer>

			<Dialog
				open={as400DialogOpen}
				onClose={handleCloseAs400}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '4px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							AS400
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: '16px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								Dados do Motorista AS400
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked
										sx={checkboxSx}
									/>
								}
								label={<Typography sx={checkboxLabelSx}>Importar dados do Funcionário</Typography>}
							/>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
									gap: '16px',
								}}
							>
								<TextField
									label='Nome'
									size='small'
									value='Aldina Fernanda Estácio da Silva'
									sx={cardFieldSx}
								/>
								<TextField
									label='Data Nascimento'
									size='small'
									value='05/07/1979'
									sx={cardFieldSx}
								/>
								<TextField
									label='Telefone Fixo'
									size='small'
									value='212222222'
									sx={cardFieldSx}
								/>
								<TextField
									label='Telemóvel'
									size='small'
									value='919999999'
									sx={cardFieldSx}
								/>
							</Box>

							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
									gap: '16px',
								}}
							>
								<TextField
									label='Nr Funcionário'
									size='small'
									value='0000489'
									sx={cardFieldSx}
								/>
								<TextField
									label='Contrato'
									size='small'
									value='Antrop'
									sx={cardFieldSx}
								/>
								<TextField
									label='Data Admissão'
									size='small'
									value='01/03/2023'
									sx={cardFieldSx}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={sectionTitleSx}>Cartão de Cidadão</Typography>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
									gap: '16px',
								}}
							>
								<TextField
									label='Nr Cartão de Cidadão'
									size='small'
									value='00000000'
									sx={cardFieldSx}
								/>
								<TextField
									label='Emissão'
									size='small'
									value='10/02/2020'
									sx={cardFieldSx}
								/>
								<TextField
									label='Validade'
									size='small'
									value='10/02/2030'
									sx={cardFieldSx}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={sectionTitleSx}>Carta de Condução</Typography>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
									gap: '16px',
								}}
							>
								<TextField
									select
									label='Tipo de Carta'
									size='small'
									value='Pesados'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={cardFieldSx}
								>
									<MenuItem value='Pesados'>Pesados</MenuItem>
									<MenuItem value='Ligeiros'>Ligeiros</MenuItem>
								</TextField>
								<TextField
									label='Direção de Viação'
									size='small'
									value='Faro'
									sx={cardFieldSx}
								/>
								<TextField
									label='Nr Carta de Condução'
									size='small'
									value='00000000'
									sx={cardFieldSx}
								/>
								<TextField
									label='Emissão'
									size='small'
									value='10/02/2020'
									sx={cardFieldSx}
								/>
								<TextField
									label='Validade'
									size='small'
									value='10/02/2030'
									sx={cardFieldSx}
								/>
							</Box>
						</Box>
					</Box>

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px' }}>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseAs400}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '14px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={servicesTotalsDialogOpen}
				onClose={handleCloseServicesTotals}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 24px 8px' }}>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 600,
							fontSize: '14px',
							lineHeight: 1.3,
							color: 'var(--primary-dark)',
						}}
					>
						Serviços e Totais por Motorista
					</Typography>

					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<TextField
							label='Data Inicial'
							size='small'
							value='02/05/2024'
							sx={{ ...cardFieldSx, maxWidth: '140px' }}
						/>
						<Typography sx={tableCellTextSx}>—</Typography>
						<TextField
							label='Data Final'
							size='small'
							value='08/05/2024'
							sx={{ ...cardFieldSx, maxWidth: '140px' }}
						/>
						<TextField
							select
							label='Filial'
							size='small'
							value='Transol'
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							sx={{ ...cardFieldSx, minWidth: '160px' }}
						>
							<MenuItem value='Transol'>Transol</MenuItem>
						</TextField>
						<TextField
							select
							label='Motorista'
							size='small'
							value='Alexandre'
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							sx={{ ...cardFieldSx, minWidth: '160px' }}
						>
							<MenuItem value='Alexandre'>Alexandre</MenuItem>
						</TextField>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked
									sx={checkboxSx}
								/>
							}
							label={<Typography sx={checkboxLabelSx}>Hora da Escala</Typography>}
						/>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								marginLeft: 'auto',
								padding: '4px 0',
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '20px',
								color: 'var(--primary-dark)',
							}}
						>
							15 Serviços
						</Typography>
						<Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
							{[
								{ label: 'Kms Carga', value: '1890' },
								{ label: 'Kms Vazio', value: '246' },
								{ label: 'Total Serviços (€)', value: '930,90' },
							].map(stat => (
								<Box
									key={stat.label}
									sx={{
										border: '1px solid rgba(134, 149, 175, 0.15)',
										borderRadius: '6px',
										padding: '6px 10px',
										minWidth: '110px',
									}}
								>
									<Typography sx={{ ...tableHeaderTextSx, fontSize: '11px' }}>{stat.label}</Typography>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '13px',
											color: 'var(--primary-dark)',
										}}
									>
										{stat.value}
									</Typography>
								</Box>
							))}
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
							<CustomButton
								size='small'
								onClick={handleShowDadosPessoais}
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '6px 12px',
									fontWeight: 600,
									fontSize: '12px',
									lineHeight: '20px',
								}}
							>
								Ficha do Motorista
							</CustomButton>
							<CustomButton
								size='small'
								onClick={handleOpenTacho}
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '6px 12px',
									fontWeight: 600,
									fontSize: '12px',
									lineHeight: '20px',
								}}
							>
								Tacógrafo
							</CustomButton>
							<ToggleButtonGroup
								value={servicesTotalsDensity}
								exclusive
								onChange={(_, value) => value && setServicesTotalsDensity(value)}
								size='small'
								sx={tableDensityToggleSx}
							>
								<ToggleButton
									value='compact'
									aria-label='densidade compacta'
								>
									<TableRowsIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: servicesTotalsDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='densidade espaçada'
								>
									<ViewHeadlineIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: servicesTotalsDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
					</Box>

					<Box
						sx={{
							border: '1px solid rgba(134, 149, 175, 0.15)',
							borderRadius: '6px',
							overflow: 'hidden',
						}}
					>
						<DataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={servicesTotalsRows}
							columns={servicesTotalsColumns}
							density={servicesTotalsDensity}
							rowHeight={servicesTotalsDensity === 'compact' ? 32 : 40}
							columnHeaderHeight={40}
							autoHeight
							disableRowSelectionOnClick
							hideFooter
							getRowClassName={params => {
								if (String(params.id) === '4') return 'row--alert'
								if (String(params.id) === '5') return 'row--warning'
								return ''
							}}
							sx={[servicesTotalsGridSx, dataGridDensityTransitionSx]}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '16px',
							marginTop: '-8px',
						}}
					>
						<Typography sx={{ ...tableHeaderTextSx, fontSize: '11px' }}>Linhas por página 10</Typography>
						<Typography sx={{ ...tableHeaderTextSx, fontSize: '11px' }}>1-5 de 13</Typography>
						<IconButton size='small'>
							<KeyboardArrowLeftRoundedIcon sx={{ color: '#00A5FF' }} />
						</IconButton>
						<IconButton size='small'>
							<KeyboardArrowRightRoundedIcon sx={{ color: '#00A5FF' }} />
						</IconButton>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: '12px',
							}}
						>
							<Typography sx={{ ...sectionTitleSx, fontSize: '18px' }}>Totais por Tipo de Serviço</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Box
									sx={{
										border: '1px solid rgba(134, 149, 175, 0.15)',
										borderRadius: '6px',
										padding: '6px 10px',
										minWidth: '120px',
									}}
								>
									<Typography sx={{ ...tableHeaderTextSx, fontSize: '11px' }}>Custos do Motorista (€)</Typography>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '13px',
											color: 'var(--primary-dark)',
										}}
									>
										0,00
									</Typography>
								</Box>
								<ToggleButtonGroup
									value={servicesTotalsDensity}
									exclusive
									onChange={(_, value) => value && setServicesTotalsDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: servicesTotalsDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: servicesTotalsDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>
						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<DataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={servicesTotalsByTypeRows}
								columns={servicesTotalsByTypeColumns}
								density={servicesTotalsDensity}
								rowHeight={servicesTotalsDensity === 'compact' ? 32 : 40}
								columnHeaderHeight={40}
								autoHeight
								disableRowSelectionOnClick
								hideFooter
								getRowClassName={params => (params.row.tipo === 'Totais' ? 'row--totals' : '')}
								sx={[servicesTotalsByTypeGridSx, dataGridDensityTransitionSx]}
							/>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							paddingBottom: '8px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseServicesTotals}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Exportar
						</CustomButton>
						<CustomButton
							size='small'
							sx={{
								background: 'var(--primary-dark)',
								color: '#FFFFFF',
								textTransform: 'none',
								borderRadius: '4px',
								padding: '6px 12px',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '20px',
							}}
						>
							Imprimir
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={driverSituationsDialogOpen}
				onClose={handleCloseDriverSituations}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '20px',
								lineHeight: 1.3,
								color: 'var(--primary-dark)',
							}}
						>
							Situações do Motorista - {driver?.name ?? 'Z-Aldina'}
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Data Inicial'
									size='small'
									value='01/05/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<Typography sx={tableCellTextSx}>—</Typography>
								<TextField
									label='Data Final'
									size='small'
									value='01/09/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<TextField
									select
									label='Situação'
									size='small'
									value='Todas'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...headerFieldSx, minWidth: '220px' }}
								>
									<MenuItem value='Todas'>Todas</MenuItem>
									<MenuItem value='Folga'>Folga</MenuItem>
									<MenuItem value='Baixa'>Baixa</MenuItem>
									<MenuItem value='Escala'>Escala</MenuItem>
								</TextField>
								<TextField
									size='small'
									label='Folga Atual'
									value='Segunda-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '220px' }}
									disabled
								/>
								<TextField
									size='small'
									label='Compl. Atual'
									value='Terça-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '220px' }}
									disabled
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								{driverSituations.length} Situações
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
								<TextField
									size='small'
									label='Total Dias'
									value={String(driverSituationsTotalDays)}
									sx={{ ...headerDisabledFieldSx, minWidth: '93px' }}
									disabled
								/>
								<CustomButton
									variant='text'
									size='small'
									sx={{
										color: '#00A5FF',
										textTransform: 'none',
										padding: '4px 0',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '22px',
									}}
									endIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
									onClick={handleToggleCreateSituation}
								>
									Criar Situação
								</CustomButton>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '24px',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
									}}
									onClick={handleShowFolgasESaldos}
								>
									Folgas Atrasadas
								</CustomButton>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '24px',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
									}}
									onClick={handleOpenCalendar}
								>
									Calendário Folgas
								</CustomButton>
								<ToggleButtonGroup
									value={driverSituationsDensity}
									exclusive
									onChange={(_, value) => value && setDriverSituationsDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: driverSituationsDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: driverSituationsDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Collapse
							in={createSituationOpen}
							timeout={250}
							unmountOnExit
						>
							<Box
								sx={{
									backgroundColor: 'rgba(245, 247, 252, 0.5)',
									borderRadius: '5px',
									padding: '16px',
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '20px',
										lineHeight: 1.3,
										color: 'var(--primary-dark)',
									}}
								>
									Criar Nova Situação
								</Typography>

								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '16px',
											minWidth: '260px',
											flex: '1 1 378px',
										}}
									>
										<TextField
											label='Data Inicial'
											size='small'
											value={newSituation.dataInicial}
											onChange={event =>
												setNewSituation(prev => ({
													...prev,
													dataInicial: event.target.value,
												}))
											}
											sx={{ ...headerFieldSx, minWidth: '140px', flex: 1 }}
										/>
										<Typography sx={tableCellTextSx}>—</Typography>
										<TextField
											label='Data Final'
											size='small'
											value={newSituation.dataFinal}
											onChange={event =>
												setNewSituation(prev => ({
													...prev,
													dataFinal: event.target.value,
												}))
											}
											sx={{ ...headerFieldSx, minWidth: '140px', flex: 1 }}
										/>
									</Box>
									<TextField
										select
										label='Situação'
										size='small'
										value={newSituation.situacao}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												situacao: event.target.value,
											}))
										}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{ ...headerFieldSx, minWidth: '220px', flex: '1 1 231px' }}
									>
										<MenuItem value='09 - Outras'>09 - Outras</MenuItem>
									</TextField>
									<TextField
										select
										label='Folga'
										size='small'
										value={newSituation.folga}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												folga: event.target.value,
											}))
										}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{ ...headerFieldSx, minWidth: '200px', flex: '1 1 231px' }}
									>
										<MenuItem value='Segunda-Feira'>Segunda-Feira</MenuItem>
									</TextField>
									<TextField
										select
										label='Complementar'
										size='small'
										value={newSituation.complementar}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												complementar: event.target.value,
											}))
										}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{ ...headerFieldSx, minWidth: '200px', flex: '1 1 231px' }}
									>
										<MenuItem value='Terça-Feira'>Terça-Feira</MenuItem>
									</TextField>
								</Box>

								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<TextField
										label='Observações'
										size='small'
										value={newSituation.observacoes}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												observacoes: event.target.value,
											}))
										}
										sx={{ ...headerFieldSx, minWidth: '260px', flex: '1 1 625px' }}
									/>
									<TextField
										select
										label='Ano Férias'
										size='small'
										value={newSituation.anoFerias}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												anoFerias: event.target.value,
											}))
										}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{ ...headerFieldSx, minWidth: '100px' }}
									>
										<MenuItem value='2025'>2025</MenuItem>
									</TextField>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												checked={newSituation.semVencimento}
												onChange={event =>
													setNewSituation(prev => ({
														...prev,
														semVencimento: event.target.checked,
													}))
												}
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Sem Vencimento</Typography>}
									/>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												checked={newSituation.contarDiasTrabalho}
												onChange={event =>
													setNewSituation(prev => ({
														...prev,
														contarDiasTrabalho: event.target.checked,
													}))
												}
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Contar só dias de Trabalho</Typography>}
									/>
								</Box>

								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
									<Typography
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '14px',
											lineHeight: 1.57,
											color: 'var(--primary-dark)',
										}}
									>
										Justificativo
									</Typography>
									<FormControlLabel
										control={
											<Checkbox
												size='small'
												checked={newSituation.entregouJustificativo}
												onChange={event =>
													setNewSituation(prev => ({
														...prev,
														entregouJustificativo: event.target.checked,
													}))
												}
												sx={checkboxSx}
											/>
										}
										label={<Typography sx={checkboxLabelSx}>Entregou Justificativo</Typography>}
									/>
									<TextField
										label='Data Entrega'
										size='small'
										value={newSituation.dataEntrega}
										onChange={event =>
											setNewSituation(prev => ({
												...prev,
												dataEntrega: event.target.value,
											}))
										}
										sx={{ ...headerFieldSx, minWidth: '166px' }}
									/>
								</Box>

								<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
									<CustomButton
										variant='text'
										size='small'
										onClick={handleCloseCreateSituation}
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											textTransform: 'none',
											padding: '4px 0',
										}}
									>
										Cancelar
									</CustomButton>
									<CustomButton
										variant='text'
										size='small'
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											textTransform: 'none',
											padding: '4px 0',
										}}
										onClick={handleAddSituation}
									>
										Adicionar
									</CustomButton>
								</Box>
							</Box>
						</Collapse>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<DataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={driverSituations}
								columns={driverSituationsColumns}
								density={driverSituationsDensity}
								rowHeight={driverSituationsDensity === 'compact' ? 32 : 40}
								columnHeaderHeight={32}
								autoHeight
								checkboxSelection
								disableRowSelectionOnClick
								hideFooter
								rowSelectionModel={selectedDriverSituations}
								onRowSelectionModelChange={selectionModel => {
									setSelectedDriverSituations([...selectionModel])
								}}
								sx={[driverSituationsGridSx, dataGridDensityTransitionSx]}
							/>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseDriverSituations}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							size='small'
							sx={{
								background: 'var(--primary-dark)',
								color: '#FFFFFF',
								textTransform: 'none',
								borderRadius: '4px',
								padding: '6px 12px',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '20px',
							}}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={sinistralidadeDialogOpen}
				onClose={handleCloseSinistralidade}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							Sinistralidade do Motorista
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '16px',
										minWidth: '260px',
									}}
								>
									<TextField
										label='Data Inicial'
										size='small'
										value='01/01/2024'
										sx={{ ...headerFieldSx, minWidth: '140px' }}
									/>
									<Typography sx={tableCellTextSx}>—</Typography>
									<TextField
										label='Data Final'
										size='small'
										value='31/12/2024'
										sx={{ ...headerFieldSx, minWidth: '140px' }}
									/>
								</Box>
								<TextField
									select
									label='Motorista'
									size='small'
									value='Z-Aldina'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...headerFieldSx, minWidth: '220px' }}
								>
									<MenuItem value='Z-Aldina'>Z-Aldina</MenuItem>
								</TextField>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked
											sx={checkboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Apenas Ativos</Typography>}
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								{sinistralidadeRows.length} Sinistros
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
								<CustomButton
									size='small'
									onClick={handleShowDadosPessoais}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
										fontWeight: 600,
										fontSize: '12px',
										lineHeight: '20px',
									}}
								>
									Ficha Motorista
								</CustomButton>
								<ToggleButtonGroup
									value={sinistralidadeDensity}
									exclusive
									onChange={(_, value) => value && setSinistralidadeDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: sinistralidadeDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: sinistralidadeDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<Table size='small'>
								<TableHead>
									<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
										{[
											'Número',
											'Data',
											'Matricula',
											'Viatura',
											'Custo Obra (€)',
											'Valor Pago (€)',
											'Valor Recebido (€)',
											'Dias Imobilização',
											'Local Sinistro',
											'Causa',
										].map(label => (
											<TableCell
												key={label}
												sx={{ padding: sinistralidadeCellPadding }}
											>
												<Typography sx={tableHeaderTextSx}>{label}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{sinistralidadeRows.map(row => (
										<TableRow key={row.id}>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.numero}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.data}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.matricula}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.viatura}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.custoObra}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.valorPago}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.valorRecebido}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.diasImobilizacao}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.localSinistro}</Typography>
											</TableCell>
											<TableCell sx={{ padding: sinistralidadeCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.causa}</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseSinistralidade}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							size='small'
							sx={{
								background: 'var(--primary-dark)',
								color: '#FFFFFF',
								textTransform: 'none',
								borderRadius: '4px',
								padding: '6px 12px',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '20px',
							}}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={activityDeclarationDialogOpen}
				onClose={handleCloseActivityDeclaration}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							Declaração de Atividade
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
								flexWrap: 'wrap',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
								<Typography
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '24px',
										lineHeight: 1.334,
										color: 'var(--primary-dark)',
									}}
								>
									Declaração de Atividade
								</Typography>
								<Typography
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: 1.57,
										textTransform: 'uppercase',
										color: 'var(--primary-dark)',
									}}
								>
									Regulamento (CE) n.º 561/2006 ou AETR(1)
								</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
										fontWeight: 600,
										fontSize: '12px',
										lineHeight: '20px',
									}}
									onClick={handleOpenServicesTotalsFromActivity}
								>
									Serviços Motorista
								</CustomButton>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
										fontWeight: 600,
										fontSize: '12px',
										lineHeight: '20px',
									}}
									onClick={handleOpenAbsentismo}
								>
									Absentismo
								</CustomButton>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '16px' }}>Folga / Complementar</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
								<TextField
									label='Folga'
									size='small'
									value='Segunda-Feira'
									sx={{ ...cardFieldSx, minWidth: '198px' }}
								/>
								<TextField
									label='Complementar'
									size='small'
									value='Terça-Feira'
									sx={{ ...cardFieldSx, minWidth: '198px' }}
								/>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked
											sx={checkboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Aniversário</Typography>}
								/>
								<TextField
									label='Data Aniversário'
									size='small'
									value='05/07/2025'
									sx={{ ...cardFieldSx, minWidth: '198px' }}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '16px' }}>Dados da Empresa Transportadora</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Nome da Empresa'
									size='small'
									value='Transol - Transportes e Turismo SA'
									sx={{ ...cardFieldSx, minWidth: '260px', flex: '1 1 380px' }}
								/>
								<TextField
									label='Telefone'
									size='small'
									value='212222222'
									sx={{ ...cardFieldSx, minWidth: '180px' }}
								/>
								<TextField
									label='Email'
									size='small'
									value='reservas@transol.pt'
									sx={{ ...cardFieldSx, minWidth: '200px' }}
								/>
								<TextField
									label='Morada'
									size='small'
									value='Rua Projectada à Caldeira do Moinho, Edf. Abicada Loja 5, 8500 - Portimão - PORTUGAL'
									sx={{ ...cardFieldSx, flex: '1 1 100%' }}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '16px' }}>
								Informações do Funcionário que faz a declaração
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Nome de quem emite a declaração'
									size='small'
									value='Rita Cisneiros'
									sx={{ ...cardFieldSx, minWidth: '260px', flex: '1 1 380px' }}
								/>
								<TextField
									label='Funções na Empresa'
									size='small'
									value='Escalador'
									sx={{ ...cardFieldSx, minWidth: '180px' }}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '16px' }}>
								Dados do Motorista para quem vai emitir a declaração
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Nome'
									size='small'
									value='Aldina Fernanda Estácio da Silva'
									sx={{ ...cardFieldSx, minWidth: '260px', flex: '1 1 320px' }}
								/>
								<TextField
									label='Data Nascimento'
									size='small'
									value='05/07/1979'
									sx={{ ...cardFieldSx, minWidth: '160px' }}
								/>
								<TextField
									select
									label='Documento Identificativo'
									size='small'
									value='Cartão de Cidadão'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...cardFieldSx, minWidth: '200px' }}
								>
									<MenuItem value='Cartão de Cidadão'>Cartão de Cidadão</MenuItem>
								</TextField>
								<TextField
									label='Nr Cartão de Cidadão'
									size='small'
									value='00000000'
									sx={{ ...cardFieldSx, minWidth: '160px' }}
								/>
								<TextField
									label='Contrato'
									size='small'
									value='Antrop'
									sx={{ ...cardFieldSx, minWidth: '160px' }}
								/>
								<TextField
									label='Data Admissão'
									size='small'
									value='01/03/2023'
									sx={{ ...cardFieldSx, minWidth: '160px' }}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Typography sx={{ ...sectionTitleSx, fontSize: '16px' }}>Período de Interrupção de Condução</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
									<TextField
										label='Data Inicial'
										size='small'
										value='05/05/2024 09:00'
										sx={{ ...cardFieldSx, minWidth: '180px' }}
									/>
									<Typography sx={tableCellTextSx}>—</Typography>
									<TextField
										label='Data Final'
										size='small'
										value='05/06/2024 09:00'
										sx={{ ...cardFieldSx, minWidth: '180px' }}
									/>
								</Box>
								<TextField
									select
									label='Tipo de Interrupção'
									size='small'
									value='16 - Gozava de baixa ou de um período de repouso'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...cardFieldSx, minWidth: '280px', flex: '1 1 360px' }}
								>
									<MenuItem value='16 - Gozava de baixa ou de um período de repouso'>
										16 - Gozava de baixa ou de um período de repouso
									</MenuItem>
								</TextField>
								<TextField
									label='Localidade'
									size='small'
									value='Portimão'
									sx={{ ...cardFieldSx, minWidth: '180px' }}
								/>
								<TextField
									label='Data'
									size='small'
									value='05/06/2024'
									sx={{ ...cardFieldSx, minWidth: '160px' }}
								/>
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseActivityDeclaration}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							size='small'
							sx={{
								background: 'var(--primary-dark)',
								color: '#FFFFFF',
								textTransform: 'none',
								borderRadius: '4px',
								padding: '6px 12px',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '20px',
							}}
						>
							Imprimir
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={absentismoDialogOpen}
				onClose={handleCloseAbsentismo}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							Consulta de Absentismo
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
									<TextField
										label='Data Inicial'
										size='small'
										value='01/05/2024'
										sx={{ ...headerFieldSx, minWidth: '140px' }}
									/>
									<Typography sx={tableCellTextSx}>—</Typography>
									<TextField
										label='Data Final'
										size='small'
										value='01/09/2024'
										sx={{ ...headerFieldSx, minWidth: '140px' }}
									/>
								</Box>
								<TextField
									select
									label='Motorista'
									size='small'
									value='Z-Aldina'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...headerFieldSx, minWidth: '220px' }}
								>
									<MenuItem value='Z-Aldina'>Z-Aldina</MenuItem>
								</TextField>
								<TextField
									size='small'
									label='Folga Atual'
									value='Segunda-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '133px' }}
									disabled
								/>
								<TextField
									size='small'
									label='Compl. Atual'
									value='Terça-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '133px' }}
									disabled
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								Absentismo
							</Typography>
							<ToggleButtonGroup
								value={absentismoDensity}
								exclusive
								onChange={(_, value) => value && setAbsentismoDensity(value)}
								size='small'
								sx={tableDensityToggleSx}
							>
								<ToggleButton
									value='compact'
									aria-label='densidade compacta'
								>
									<TableRowsIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: absentismoDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='densidade espaçada'
								>
									<ViewHeadlineIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: absentismoDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<Table size='small'>
								<TableHead>
									<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
										{['Início', 'Fim', 'Difer', 'Descrição', 'Importado', 'Situação'].map(label => (
											<TableCell
												key={label}
												sx={{ padding: '6px 16px' }}
											>
												<Typography sx={tableHeaderTextSx}>{label}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
							</Table>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px',
								padding: '7px 16px',
								borderRadius: '4px',
								backgroundImage: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
							}}
						>
							<InfoOutlinedIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '14px',
									fontWeight: 500,
									lineHeight: 1.5,
									letterSpacing: '0.15px',
									color: '#FFFFFF',
								}}
							>
								Sem informação a apresentar.
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseAbsentismo}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={restAfterWorkDialogOpen}
				onClose={handleCloseRestAfterWork}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '6px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							Descansos após Folga / Complementar Trabalhada
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
								<TextField
									label='Data Inicial'
									size='small'
									value='01/01/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<Typography sx={tableCellTextSx}>—</Typography>
								<TextField
									label='Data Final'
									size='small'
									value='31/12/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<TextField
									select
									label='Motorista'
									size='small'
									value='Z-Aldina'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...headerFieldSx, minWidth: '220px' }}
								>
									<MenuItem value='Z-Aldina'>Z-Aldina</MenuItem>
								</TextField>
								<TextField
									size='small'
									label='Folga Atual'
									value='Segunda-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '133px' }}
									disabled
								/>
								<TextField
									size='small'
									label='Compl. Atual'
									value='Terça-Feira'
									sx={{ ...headerDisabledFieldSx, minWidth: '133px' }}
									disabled
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								{descansosAposFolgaRows.length} Registos
							</Typography>
							<ToggleButtonGroup
								value={restAfterWorkDensity}
								exclusive
								onChange={(_, value) => value && setRestAfterWorkDensity(value)}
								size='small'
								sx={tableDensityToggleSx}
							>
								<ToggleButton
									value='compact'
									aria-label='densidade compacta'
								>
									<TableRowsIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: restAfterWorkDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='densidade espaçada'
								>
									<ViewHeadlineIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: restAfterWorkDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<Table size='small'>
								<TableHead>
									<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
										{[
											'Data Mov',
											'Tipo',
											'Descrição',
											'Folga Trab',
											'Compl Trab',
											'Descanso',
											'Saldo',
											'ID Folgas Config',
											'Cód Mov',
										].map(label => (
											<TableCell
												key={label}
												sx={{ padding: restAfterWorkCellPadding }}
											>
												<Typography sx={tableHeaderTextSx}>{label}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{descansosAposFolgaRows.map(row => (
										<TableRow key={row.id}>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.dataMov}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.tipo}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.descricao}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.folgaTrabalhada}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.complementarTrabalhada}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.descanso}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.saldo}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.idConfig}</Typography>
											</TableCell>
											<TableCell sx={{ padding: restAfterWorkCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.codMov}</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseRestAfterWork}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Dialog
				open={tachoDialogOpen}
				onClose={handleCloseTacho}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '4px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: 'var(--primary-dark)',
							}}
						>
							Digitalização dos Discos de Tacógrafo
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Data Inicial'
									size='small'
									value='01/01/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<Typography sx={tableCellTextSx}>—</Typography>
								<TextField
									label='Data Final'
									size='small'
									value='31/12/2024'
									sx={{ ...headerFieldSx, minWidth: '140px' }}
								/>
								<TextField
									select
									label='Motorista'
									size='small'
									value='Z-Aldina'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...headerFieldSx, minWidth: '180px' }}
								>
									<MenuItem value='Z-Aldina'>Z-Aldina</MenuItem>
								</TextField>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked
											sx={checkboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Apenas Ativos</Typography>}
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: 'var(--primary-dark)',
								}}
							>
								2 Registos
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<CustomButton
									size='small'
									onClick={handleShowDadosPessoais}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
										fontWeight: 600,
										fontSize: '12px',
										lineHeight: '20px',
									}}
								>
									Ficha Motorista
								</CustomButton>
								<ToggleButtonGroup
									value={tachoDensity}
									exclusive
									onChange={(_, value) => value && setTachoDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tachoDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tachoDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<Table size='small'>
								<TableHead>
									<TableRow sx={{ borderBottom: '1px solid rgba(134, 149, 175, 0.15)' }}>
										{['Núm Mec', 'Data', 'Data Real', 'Nome Ficheiro', 'Nome'].map(label => (
											<TableCell
												key={label}
												sx={{ padding: tachoCellPadding }}
											>
												<Typography sx={tableHeaderTextSx}>{label}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{tachoRows.map(row => (
										<TableRow key={row.id}>
											<TableCell sx={{ padding: tachoCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.numMec}</Typography>
											</TableCell>
											<TableCell sx={{ padding: tachoCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.data}</Typography>
											</TableCell>
											<TableCell sx={{ padding: tachoCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.dataReal}</Typography>
											</TableCell>
											<TableCell sx={{ padding: tachoCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.nomeFicheiro}</Typography>
											</TableCell>
											<TableCell sx={{ padding: tachoCellPadding }}>
												<Typography sx={tableCellTextSx}>{row.nome}</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseTacho}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							size='small'
							sx={{
								background: 'var(--primary-dark)',
								color: '#FFFFFF',
								textTransform: 'none',
								borderRadius: '4px',
								padding: '6px 12px',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '20px',
							}}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>
		</Box>
	)
})

export default SchedulesDriverPage
